/**
 * AtlasWorldMapModule — the World Atlas (N): a pan/zoom map of the Commons
 * with every settlement named, civ-colored, and clickable into a FULL stats
 * panel — governance, laws, people, economy, wars, and the versonas who live
 * there — plus a Civilizations tab aggregating both nations.
 *
 * Data doctrine: display only, the server is the authority on every row.
 * Cheap 312-row tables subscribe when the window first opens; per-settlement
 * detail (agents, stock, laws, graves, chronicle…) subscribes lazily WHERE
 * settlement_id = <sid> on selection (the AtlasChronicleModule pattern), and
 * the Civilizations tab pulls the two global tables it aggregates
 * (npc_agent, grave) only when first shown.
 *
 * Names: `settlement_name` rows are sparse admin renames — everything else
 * derives deterministically from the seed (lib/settlement_names.ts), so every
 * client reads the identical atlas. The terrain underlay is sampled from the
 * REAL height/biome fields (shared/terrain) in async slices per view.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { settlementSites, type SettlementSite } from '@shared/settlements.js';
import { BATTLE } from '@shared/living_events.js';
import { civilizationOf, type Civilization } from '../lib/biome_settlements.js';
import { civOfSettlementRowC, civRowGeneration } from '../lib/civ_row.js';
import { settlementNamesFor } from '../lib/settlement_names.js';
import { peekCircuit } from '../lib/race_circuit.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { tempColor, humidityColor, WEATHER_CLASS_COLOR, type WeatherClass } from '../lib/weather.js';
import { currentTod } from './SkyModule.js';
import { liveEventMarkers, eventMarkerAt, type EventMarker } from '../lib/event_markers.js';
import { waypointToEvent } from '../lib/event_waypoint.js';
import { EVENT_KINDS as LIVING_EVENT_KINDS } from '@shared/living_events.js';
import { myWaypoints, promptCreateWaypoint, promptEditWaypoint, drawWaypointMarker, trackedWaypoint, trackWaypoint, waypointAt, WAYPOINT_COLOR } from '../lib/waypoints.js';
import { StructureLayer, MAX_MAP_SCALE, LABEL_ZOOM, STREETS_ZOOM, type MapView } from './worldmap/StructureLayer.js';
import { FindPanel } from './worldmap/FindPanel.js';
import type { MapEntry } from '../lib/map_structures.js';
import { MapRaster, rasterPoolSize } from '../lib/map_raster.js';
import type { ConflictSnapshot } from '../lib/map_layers.js';
import { binHeat, heatCellM, drawHeat, PEOPLE_PALETTE, CREDITS_PALETTE, RESOURCES_PALETTE, type HeatPoint } from '../lib/map_heat.js';
import { liveFlows, marketLinkLines, alongLeg, type FlowRow, type Flow, type LinkLine } from '../lib/trade_map.js';
import { routeSegments } from '../lib/regional_routes.js';
import { tableStamp } from '../lib/fast_find.js';
import { officeLabel } from '../lib/offices_wave.js';
import { isServiceKey } from '../lib/service_posts.js';
import { TIER_VOTES } from '../lib/federal_core.js';
import { themeColor, onThemeChange } from '../lib/ui_theme.js';
import { claimsFor } from '../lib/guild_claims.js';
import { TRADE_GUILDS } from '../lib/guild_rules.js';

const rowFirstCiv = (siteSeed: number, siteId?: number): Civilization =>
  siteId === undefined ? civilizationOf(siteSeed) : civOfSettlementRowC(BigInt(siteId + 1), siteSeed);

// ── Row shapes (display-only mirrors of the server schema) ────────────────────
interface Ts { microsSinceUnixEpoch: bigint }
interface NameRow { siteId: bigint; name: string }
interface CivRow { settlementId: bigint; civ: string }
interface EconRow {
  settlementId: bigint; centerX: number; centerZ: number;
  stockFood: number; stockOre: number; stockTools: number; stockLuxury: number;
  priceFood: number; priceOre: number; priceTools: number; priceLuxury: number;
  treasury: bigint; taxBps: number; mayorId: bigint; nextElectionAt: Ts;
  projectKey: string; projectProgress: number; projectTarget: number;
}
interface OfficeRow { settlementId: bigint; office: string; holderId: bigint; holderName: string; sinceAt: Ts }
interface WarRow { aggressorSid: bigint; defenderSid: bigint; status: string; grievanceAtStart: number; exhaustionA: number; exhaustionB: number; casualties: number; startedAt: Ts }
interface RivalryRow { aId: bigint; bId: bigint; grievance: number }
// SOCIETY WAVE (2026-09-19): the land, the town's scores and its seat in the Assembly.
interface NodeRow { settlementId: bigint; good: string; x: number; z: number; richness: number }
interface ScoreRow { settlementId: bigint; cohesion: number; readiness: number; learning: number; health: number; order: number; piety: number; prosperity: number }
interface MemberRow { settlementId: bigint; civ: string; tier: string; votes: number; suspended: boolean; defiant: boolean; arrears: bigint }
interface LinkRow { settlementId: bigint; citySid: bigint }
interface SpecRow { settlementId: bigint; spec: string }
interface SlotRow { settlementId: bigint; recipeKey: string; holderId: bigint }
interface AgentRow { versonaId: bigint; settlementId: bigint; job: string; wallet: bigint; hunger: number; mood: number; needRest: number }
interface VersonaRow { versonaId: bigint; homeCityId: bigint; curX: number; curZ: number; state: string }
interface HouseholdRow { householdId: bigint; settlementId: bigint; surname: string; headId: bigint }
interface HouseholdMemberRow { householdId: bigint; versonaId: bigint }
interface FaithRow { versonaId: bigint; faithId: string; intensity: number }
interface GraveRow { settlementId: bigint; name: string; cause: string; diedAt: Ts }
interface JobSlotRow { settlementId: bigint; fn: string; recipeKey: string; wage: number; holderId: bigint }
interface StockRow { settlementId: bigint; commodity: string; qty: number }
interface RoundRow { settlementId: bigint; commodity: string; avgPrice: number; volume: number; unfilled: number; epoch: number }
interface ClauseRow { settlementId: bigint; kind: string; value: number; status: string; rationale: string; yesVotes: number }
interface NormRow { settlementId: bigint; key: string; text: string; adoptionX10: number; active: boolean }
interface ResidencyRow { settlementId: bigint; paidUntil: Ts }
interface ExchangeRow { settlementId: bigint }

const CIV_COLOR: Record<Civilization, string> = { a: '#d9a94e', b: '#7fc4e8' };
const CIV_TITLE: Record<Civilization, string> = { a: 'Aurelian Concord', b: 'Borean Compact' };
const CIV_FOLK: Record<Civilization, string> = { a: 'Aurelians', b: 'Boreans' };
const TIER_R: Record<string, number> = { city: 7, town: 4.5, hamlet: 2.8, capital: 12, district: 0, circuit: 6 }; // district 0 = drawn as its capital
/** The map's layer modes: the sampled rasters (weather field, conflict), the heat layers (people, economy). One at a time. */
type MapMode = 'none' | 'temp' | 'weather' | 'humidity' | 'civ' | 'war' | 'people' | 'economy';
const RASTER_MODES: ReadonlySet<MapMode> = new Set<MapMode>(['temp', 'weather', 'humidity', 'civ', 'war']);
interface PlayerRow { identity: { toHexString(): string }; worldId: bigint; posX: number; posZ: number }
interface PresenceRow { identity: { toHexString(): string }; online: boolean }
interface AccountRow { identity: { toHexString(): string }; username: string }
interface LedgerRow { settlementId: bigint; fn: string; cash: bigint }
interface CaravanRow { fromSid: bigint; toSid: bigint; commodity: string; qty: number; departAt: Ts; arriveAt: Ts; status: string }
interface ConvoyRow { fromSid: bigint; toSid: bigint; commodity: string; qty: number; departAt: Ts; arriveAt: Ts; status: string; fromX: number; fromZ: number; toX: number; toZ: number }
interface MarketLinkRow { settlementId: bigint; citySid: bigint; trips: number }
/** People: a versona's warmth on the map; a traveler (online player) counts for several — you can see them. */
const PLAYER_HEAT_WEIGHT = 4;
const OFFICE_LABEL: Record<string, string> = { keeper: 'Keeper of the Granary', guildmaster: 'Guildmaster' };

/** DB id space: settlement rows key settlementId = site.id + 1 (0 = none/wilds). */
const dbSid = (siteId: number): number => siteId + 1;

const esc = (v: string): string => v.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const coin = (v: bigint | number): string => `ⓒ${Number(v).toLocaleString()}`;
const pretty = (k: string): string => k.replace(/_/g, ' ');
const inH = (t: Ts): string => {
  const ms = Number(t.microsSinceUnixEpoch / 1000n) - Date.now();
  if (ms <= 0) return 'now';
  return ms < 3_600_000 ? `in ${Math.round(ms / 60000)}m` : `in ${Math.round(ms / 3_600_000)}h`;
};
const ago = (t: Ts): string => {
  const s = Math.max(0, (Date.now() - Number(t.microsSinceUnixEpoch / 1000n)) / 1000);
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return s < 86400 ? `${Math.round(s / 3600)}h ago` : `${Math.round(s / 86400)}d ago`;
};

export class AtlasWorldMapModule implements IModule {
  readonly name = 'AtlasWorldMap';

  private win: UiWindow | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private wrap: HTMLDivElement | null = null;
  private tip: HTMLDivElement | null = null;
  private panel: HTMLDivElement | null = null;
  private search = '';
  private tab: 'settlement' | 'civ' | 'find' = 'settlement';
  /** Which tab's DOM the panel holds (the Find tab keeps its own DOM across refreshes). */
  private panelMode = '';
  /** BUILDING ZOOM (2026-09-24): the streets, buildings and named places, and the index the Find tab searches. */
  private readonly layer: StructureLayer;
  private find: FindPanel | null = null;
  private hoverEntry: MapEntry | null = null;
  private tipEntry: MapEntry | null = null;
  /** SWG shuttle mode: clicking a city/town books the hop instead of only
   *  selecting it (opened from a shuttle pad via AtlasTravelModule). */
  private travelMode = false;
  private travelBanner: HTMLDivElement | null = null;

  private seed = '';
  private civGen = -1;
  private sites: SettlementSite[] = [];
  private derivedNames = new Map<number, string>();
  private selected: SettlementSite | null = null;
  private hovered: SettlementSite | null = null;
  private tipFor: SettlementSite | null = null;

  private view = { cx: 0, cz: 0, scale: 0.001 }; // px per meter
  /** fitAll once the canvas has a size — `ensureWorld` runs before `build()` on the first open (2026-09-21: the map opened at 5.9e-7 px/m). */
  private fitPending = false;
  private dirty = true;
  private raf = 0;
  private timer: number | null = null;
  private openSubbed = false;
  private globalJoinsSubbed = false;
  private civTabSubbed = false;
  private readonly sidSubbed = new Set<string>();

  // The tiled raster renderer (2026-09-21): the terrain underlay and every
  // sampled layer are world-aligned tiles rendered by a worker pool and cached
  // across pans and zooms — lib/map_raster.ts. Built with the window.
  private raster: MapRaster | null = null;
  private sea = 0;

  /** The active layer (one at a time) — the weather field, the conflict layers, or a heat layer. */
  private wxMode: MapMode = 'none';
  /** The trade layer stacks on any mode (roads, market habits, live caravans and sky convoys). */
  private tradeOn = false;
  /** true while something on the map animates (a live battle's dot, a moving caravan) — the loop redraws every frame */
  private pulse = false;
  private wxButtons = new Map<string, HTMLButtonElement>();
  private tradeButton: HTMLButtonElement | null = null;
  /** GUILDS (2026-09-24): every guild's claim and hall — its own small system, drawn smaller than a hamlet; stacks on any mode. */
  private guildsOn = false;
  private guildButton: HTMLButtonElement | null = null;
  private peopleSubbed = false;
  /** The people layer's `player_state` subscription — held so it can END (bug sweep 2026-09-23): kept forever, it
   *  put every online traveler in the Commons into the row cache, where the network module made each one a remote
   *  (a full avatar, built and grounded every frame) that never left, however far away. */
  private peopleSubHandle: { unsubscribe(): void } | null = null;
  /** The people layer's `npc_agent` subscription (the hover card's "N folk") — 2026-09-25: its handle was discarded, so
   *  every re-enable / reopen stacked ANOTHER whole-table subscription (6–11k rows re-downloaded each time). */
  private peopleAgentHandle: { unsubscribe(): void } | null = null;
  /** Per-frame memos (bug sweep 2026-09-23): the draw ran every rAF frame while trade flows or a battle pulsed,
   *  and walked whole tables for each visible site (settlement_civ per site ≈ 140k steps, ~8 ms a frame). */
  private namesMemo: { stamp: number; at: number; m: Map<number, string> } | null = null;
  private dotsMemo: { sid: number; at: number; ids: bigint[] } | null = null;
  private warMemo: { at: number; live: Array<{ x: number; z: number; n: number }>; fought: Array<{ x: number; z: number; n: number; resolvedS: number }> } | null = null;
  /** The heat layer drawn once per (view, data refresh) and blitted on every other frame. */
  private heatCache: { key: string; canvas: HTMLCanvasElement; cx: number; cz: number; mx: number; my: number } | null = null;
  /** One weather clock per 60 s bucket: every tile of a bucket is the same instant (fronts no longer step at tile borders). */
  private wxClock = { bucket: -1, tod: 0 };
  private econSubbed = false;
  private tradeSubbed = false;
  /** The last heat / trade census (the lever and the legend read it). */
  private census: { versonas: number; players: number; credits: number; resources: number; flows: number; links: number; roads: number; guilds: number; guildLabels: number; guildPlayer: number; guildVersona: number; heatPaints?: number } = { versonas: 0, players: 0, credits: 0, resources: 0, flows: 0, links: 0, roads: 0, guilds: 0, guildLabels: 0, guildPlayer: 0, guildVersona: 0, heatPaints: 0 };
  /** Row walks are memoised by wall clock: a dirty frame must not re-read 12k versona rows (the pulse redraws every frame). */
  private memo = { people: { at: 0, v: null as null | { versonas: HeatPoint[]; players: Array<{ x: number; z: number; name: string }> } }, econ: { at: 0, v: null as null | Map<number, { credits: number; resources: number }> }, trade: { at: 0, rows: null as null | FlowRow[], links: null as null | LinkLine[] }, conflict: { at: 0, v: null as null | ConflictSnapshot, digest: '', civ: '', war: '' } };

  constructor() {
    this.layer = new StructureLayer({
      seed: () => this.seed,
      sites: () => this.sites,
      nameOf: (s) => this.nameOf(s),
      civOf: (s) => this.civOf(s),
    });
    this.layer.onChange = (): void => { this.dirty = true; if (this.tab === 'find') this.find?.refresh(true); };
  }

  init(_app: App): void {
    void _app;
    // gaps wave (2026-09-28): the legends, chips and label face are painted through themeColor — nothing listened for a
    // theme change, so an open map kept the old theme until something else dirtied it. The loop redraws a dirty map.
    onThemeChange(() => { this.dirty = true; });
    window.addEventListener('keydown', (e) => {
      if (e.code !== 'KeyN' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (atlas.loadingGate || atlas.currentWorldId === null || atlas.spaceFlight) return; // in space N cycles nav targets (key-collision audit 2026-08-24)
      this.toggle();
    });
    // A window elsewhere asks to show a place (the Guild window's "Show on map"): open, centre, and the guild filter.
    bus.on('atlas:map-focus', ({ x, z, zoom, guilds }) => {
      if (!this.win?.isOpen()) this.toggle();
      this.fitPending = false;
      this.view.cx = x; this.view.cz = z; this.view.scale = Math.min(MAX_MAP_SCALE, Math.max(0.00018, zoom ?? 1));
      if (guilds) this.setGuilds(true);
      this.dirty = true;
    });
    // Diagnostic surface (headless probes — no stable DOM ids on map buttons):
    // open the window, flip the weather layer, and read the build state.
    (window as unknown as Record<string, unknown>).__wxMap = {
      open: (): void => { if (!this.win?.isOpen()) this.toggle(); },
      close: (): void => { if (this.win?.isOpen()) this.toggle(); },
      set: (m: MapMode): void => this.setWxMode(m),
      setTrade: (on: boolean): void => this.setTrade(on),
      setGuilds: (on: boolean): void => this.setGuilds(on),
      guilds: () => ({ on: this.guildsOn, drawn: this.census.guilds, labels: this.census.guildLabels, player: this.census.guildPlayer, versona: this.census.guildVersona }),
      /** `built` = every visible tile of the active layer is exact; `building` = tiles queued or in flight. */
      state: (): { mode: string; trade: boolean; built: boolean; building: boolean; winOpen: boolean; seed: boolean } => {
        const st = this.raster?.stats;
        const busy = !!st && (st.queued > 0 || st.inFlight > 0);
        const rasterMode = RASTER_MODES.has(this.wxMode);
        // `built` speaks for the ACTIVE layer: the counts are the last draw call's, and the terrain draws first each frame.
        const ofLayer = !!st && st.layer === this.wxMode;
        return { mode: this.wxMode, trade: this.tradeOn, built: rasterMode ? ofLayer && st.missing === 0 && st.fallback === 0 && !busy : this.wxMode !== 'none', building: rasterMode && (busy || !ofLayer), winOpen: !!this.win?.isOpen(), seed: this.seed !== '' };
      },
      raster: () => (this.raster ? { ...this.raster.stats } : null),
      census: () => ({ ...this.census }),
      view: () => ({ ...this.view, w: this.cw(), h: this.ch() }),
      setView: (cx: number, cz: number, scale: number): void => { this.view.cx = cx; this.view.cz = cz; this.view.scale = Math.min(MAX_MAP_SCALE, Math.max(0.00018, scale)); this.dirty = true; },
      draw: (): void => this.draw(),
      /** BUILDING ZOOM: the layer's state and the last draw's numbers. */
      structures: () => ({ status: this.layer.status, entries: this.layer.entries().length, census: { ...this.layer.census } }),
      /** What the cursor would pick at canvas point (px, py): the structure (or null) and the hover card's text. */
      pickAt: (px: number, py: number): { id: string; name: string; category: string; card: string } | null => {
        const e = this.layer.pick(this.mapView(), px, py);
        return e ? { id: e.id, name: e.name, category: e.category, card: this.layer.cardHtml(e, atlas.localX, atlas.localZ).replace(/<[^>]+>/g, ' ') } : null;
      },
      /** Open the Find tab with these words (and kinds); returns the query's numbers once it has run. */
      find: (text: string, categories: string[] = []): { total: number; shown: number; first: string | null; names: string[] } => {
        this.openFind(text);
        const f = this.find;
        if (!f) return { total: 0, shown: 0, first: null, names: [] };
        f.setCategories(categories as MapEntry['category'][]);
        const names = [...f.root.querySelectorAll('.wf-item .wf-name')].slice(0, 10).map((n) => n.textContent ?? '');
        return { total: f.stats.total, shown: f.stats.shown, first: names[0] ?? null, names };
      },
      /** Click the n-th result's ⌖ (fly) or ◆ (waypoint). */
      findAct: (i: number, act: 'fly' | 'wp'): boolean => {
        const b = this.find?.root.querySelector(`.wf-item[data-i="${i}"] [data-act="${act}"]`) as HTMLButtonElement | null;
        if (!b) return false;
        b.click();
        return true;
      },
      /** Pin the structure under canvas point (px, py) as a click would. */
      clickAt: (px: number, py: number): string => this.clickMap(px, py),
    };
  }

  // ── Data access ───────────────────────────────────────────────────────────
  private tbl<T>(name: string): T[] {
    try {
      const t = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<T> } | undefined>)[name];
      return t ? [...t.iter()] : [];
    } catch { return []; }
  }

  private sub(sql: string[]): void {
    // One builder per query: a rejected query (missing table on an older
    // publish, unindexed column) must not sink its siblings in the batch.
    for (const q of sql) {
      try { atlas.conn?.subscriptionBuilder().subscribe([q]); } catch { /* pre-publish server */ }
    }
  }

  private ensureWorld(): boolean {
    const conn = atlas.conn;
    if (!conn) return false;
    for (const w of this.tbl<{ kind: string; terrainSeed: string }>('world')) {
      if (w.kind !== 'commons') continue;
      if (this.seed !== w.terrainSeed || this.civGen !== civRowGeneration()) {
        const newWorld = this.seed !== w.terrainSeed;
        this.civGen = civRowGeneration();
        this.seed = w.terrainSeed;
        this.sites = settlementSites(this.seed);
        this.derivedNames = settlementNamesFor(this.sites, rowFirstCiv);
        this.sea = commonsSeaLevel(this.seed);
        this.raster?.setWorld(this.seed, this.sea);
        if (newWorld) void this.layer.ensure(); else this.layer.rebuildNames();
        // Fit only a NEW world (the canvas may not exist yet — the first real draw fits; cw() is 1 before build).
        // A conquest bumps the civ generation too, and re-fitting on it threw away the player's zoom and pan
        // on the next open (bug sweep 2026-09-23).
        if (newWorld) this.fitPending = true;
      }
      return true;
    }
    return false;
  }

  private ensureOpenSubs(): void {
    if (this.openSubbed) return;
    this.openSubbed = true;
    this.sub([
      'SELECT * FROM settlement_name', 'SELECT * FROM settlement_civ',
      'SELECT * FROM settlement_econ', 'SELECT * FROM settlement_office',
      'SELECT * FROM war_state', 'SELECT * FROM settlement_rivalry', 'SELECT * FROM war_assignment',
      'SELECT * FROM battle', 'SELECT * FROM battle_unit', 'SELECT * FROM battle_record',
      'SELECT * FROM exchange_office', 'SELECT * FROM residency',
    ]);
  }

  private readonly sidOrder: string[] = [];
  private readonly sidHandles = new Map<string, Array<{ unsubscribe(): void }>>();
  /** sub() variant that keeps handles so a settlement's queries can release. */
  private subH(sql: string[]): Array<{ unsubscribe(): void }> {
    const out: Array<{ unsubscribe(): void }> = [];
    for (const q of sql) {
      try {
        const h = atlas.conn?.subscriptionBuilder().subscribe([q]) as { unsubscribe(): void } | undefined;
        if (h) out.push(h);
      } catch { /* pre-publish server */ }
    }
    return out;
  }
  private ensureSidSubs(sid: bigint): void {
    const k = String(sid);
    if (this.sidSubbed.has(k)) return;
    this.sidSubbed.add(k);
    // (bug sweep) LRU: clicking through N settlements stacked 9 live queries
    // each with no release path — cap at 6 towns, oldest unsubscribes.
    // (the dead `versona WHERE home_city_id` query is gone — the column is 0.)
    this.sidOrder.push(k);
    while (this.sidOrder.length > 6) {
      const old = this.sidOrder.shift()!;
      this.sidSubbed.delete(old);
      for (const h of this.sidHandles.get(old) ?? []) { try { h.unsubscribe(); } catch { /* gone */ } }
      this.sidHandles.delete(old);
    }
    this.sidHandles.set(k, this.subH([
      `SELECT * FROM npc_agent WHERE settlement_id = ${sid}`,
      `SELECT * FROM household WHERE settlement_id = ${sid}`,
      `SELECT * FROM grave WHERE settlement_id = ${sid}`,
      `SELECT * FROM job_slot WHERE settlement_id = ${sid}`,
      `SELECT * FROM settlement_stock WHERE settlement_id = ${sid}`,
      `SELECT * FROM market_round WHERE settlement_id = ${sid}`,
      `SELECT * FROM charter_clause WHERE settlement_id = ${sid}`,
      `SELECT * FROM settlement_norm WHERE settlement_id = ${sid}`,
      // society wave: the land (deposit nodes), the seven scores, the federal seat, the trading habit
      `SELECT * FROM resource_node WHERE settlement_id = ${sid}`,
      `SELECT * FROM settlement_score WHERE settlement_id = ${sid}`,
      `SELECT * FROM federal_member WHERE settlement_id = ${sid}`,
      `SELECT * FROM market_link WHERE settlement_id = ${sid}`,
      `SELECT * FROM city_spec WHERE settlement_id = ${sid}`,
    ]));
    if (!this.globalJoinsSubbed) {
      // Joins with no settlement column (member→household, faith per versona).
      this.globalJoinsSubbed = true;
      this.sub(['SELECT * FROM household_member', 'SELECT * FROM faith_adherence']);
    }
  }

  private ensureCivTabSubs(): void {
    if (this.civTabSubbed) return;
    this.civTabSubbed = true;
    this.sub(['SELECT * FROM npc_agent', 'SELECT * FROM grave']);
  }

  private nameOf(s: SettlementSite): string {
    // Admin renames, as a map rebuilt when the table moves (a whole-table copy per site per frame before).
    const tbl = (atlas.conn?.db as unknown as Record<string, unknown> | undefined)?.settlementName;
    const stamp = tableStamp(tbl), now = performance.now();
    const fresh = this.namesMemo && (Number.isNaN(stamp) ? now - this.namesMemo.at < 1000 : this.namesMemo.stamp === stamp);
    if (!fresh) {
      const m = new Map<number, string>();
      for (const n of this.tbl<NameRow>('settlementName')) m.set(Number(n.siteId), n.name);
      this.namesMemo = { stamp, at: now, m };
    }
    return this.namesMemo!.m.get(s.id) ?? this.derivedNames.get(s.id) ?? `settlement #${s.id}`;
  }

  private civOf(s: SettlementSite): Civilization {
    return civOfSettlementRowC(dbSid(s.id), s.siteSeed); // the indexed row, seed as the fallback (row-first)
  }

  private econOf(s: SettlementSite): EconRow | undefined {
    try {
      const t = (atlas.conn?.db as unknown as { settlementEcon?: { settlementId: { find(id: bigint): EconRow | undefined } } } | undefined)?.settlementEcon;
      if (t) return t.settlementId.find(BigInt(dbSid(s.id))) ?? undefined; // the PK, not a table copy
    } catch { /* pre-publish server */ }
    return this.tbl<EconRow>('settlementEcon').find((e) => Number(e.settlementId) === dbSid(s.id));
  }

  private warsOf(siteId: number): WarRow[] {
    const id = dbSid(siteId);
    return this.tbl<WarRow>('warState').filter((w) =>
      w.status === 'declared' && (Number(w.aggressorSid) === id || Number(w.defenderSid) === id));
  }

  private siteById(id: number): SettlementSite | undefined {
    return this.sites.find((s) => s.id === id);
  }

  // ── Window ────────────────────────────────────────────────────────────────
  private toggle(): void {
    if (this.win?.isOpen()) { this.win.setOpen(false); this.travelMode = false; this.syncTravelBanner(); this.stopLoops(); return; }
    if (!this.ensureWorld()) return;
    this.ensureOpenSubs();
    if (!this.win) this.build();
    this.win!.setOpen(true);
    this.dirty = true;
    this.startLoops();
    this.renderPanel();
  }

  /** Open in SHUTTLE mode (AtlasTravelModule, from a pad): pick a city or town
   *  to book the 10 SOV hop; hamlets stay visible but unbookable. */
  openTravel(): void {
    if (!this.ensureWorld()) return;
    this.ensureOpenSubs();
    if (!this.win) this.build();
    this.travelMode = true;
    this.win!.setOpen(true);
    this.syncTravelBanner();
    this.dirty = true;
    this.startLoops();
    this.renderPanel();
  }

  private syncTravelBanner(): void {
    if (!this.travelBanner) return;
    this.travelBanner.style.display = this.travelMode ? 'block' : 'none';
  }

  private async bookShuttle(s: SettlementSite): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return;
    try {
      await conn.reducers.shuttleTravel({ targetSiteId: BigInt(s.id) });
    } catch (e) {
      if (this.travelBanner) this.travelBanner.textContent = `⚠ ${String((e as Error).message ?? e)}`;
      return;
    }
    this.travelMode = false;
    this.syncTravelBanner();
    this.win?.setOpen(false);
    this.stopLoops();
    const me = conn.identity ? conn.db.playerState.identity.find(conn.identity) : null;
    // Same world — adopt re-centers AOI + local transform on the arrival pad.
    if (atlas.currentWorldId !== null) {
      bus.emit('atlas:world-adopted', { worldId: atlas.currentWorldId, x: me?.posX ?? s.centerX, z: me?.posZ ?? s.centerZ });
    }
    bus.emit('atlas:status', { text: `Shuttle to ${this.nameOf(s)} — 10 ⓒ` });
  }

  private build(): void {
    this.win = makeWindow({
      id: 'worldmap', title: 'World Atlas', icon: 'map',
      width: 1040, height: 640, minWidth: 760, minHeight: 460, x: 60, y: 48,
      onClose: () => this.stopLoops(),
    });
    const root = document.createElement('div');
    root.style.cssText = 'display:flex;flex-direction:column;height:100%;font:12px/1.45 var(--ax-font);color:var(--ax-text)';

    // Toolbar
    const bar = document.createElement('div');
    bar.style.cssText = 'display:flex;gap:8px;align-items:center;padding:6px 8px;border-bottom:1px solid var(--ax-line);flex:0 0 auto';
    const inp = document.createElement('input');
    inp.placeholder = 'find a settlement…';
    inp.style.cssText = 'flex:0 0 200px;background:var(--ax-bg2);border:1px solid var(--ax-line);border-radius:var(--ax-radius);color:var(--ax-text);padding:4px 8px;font:12px var(--ax-font)';
    inp.addEventListener('input', () => { this.search = inp.value.trim().toLowerCase(); this.dirty = true; });
    inp.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key !== 'Enter' || !this.search) return;
      const hit = this.sites.find((s) => this.nameOf(s).toLowerCase().includes(this.search));
      if (hit) { this.select(hit); this.center(hit.centerX, hit.centerZ); }
      else this.openFind(inp.value.trim()); // no settlement by that name: search the structures
    });
    bar.appendChild(inp);
    const findBtn = document.createElement('button');
    findBtn.textContent = '🔍 find';
    findBtn.title = 'Find a structure — clinics, cantinas, armories, starports, player houses and cities, anywhere on the map';
    findBtn.dataset.mapFind = '1';
    findBtn.style.cssText = 'background:var(--ax-bg3);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:3px 10px;cursor:pointer';
    findBtn.onclick = () => this.openFind();
    bar.appendChild(findBtn);
    const legend = document.createElement('div');
    legend.innerHTML =
      `<span style="color:${CIV_COLOR.a}">●</span> Aurelian Concord&nbsp;&nbsp;` +
      `<span style="color:${CIV_COLOR.b}">●</span> Borean Compact&nbsp;&nbsp;` +
      '<span style="opacity:.6">◉ city · ● town · • hamlet — click a settlement; scroll in to see every building · ' +
      '<span style="color:var(--ax-hi)">◆</span> right-click sets a waypoint (click one to follow it, right-click to rename/delete)</span>';
    legend.style.cssText = 'flex:1;opacity:.9;white-space:nowrap;overflow:hidden;text-overflow:ellipsis';
    bar.appendChild(legend);
    const meBtn = document.createElement('button');
    meBtn.textContent = '⌖ me';
    meBtn.title = 'Center on your position';
    meBtn.style.cssText = 'background:var(--ax-bg3);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:3px 10px;cursor:pointer';
    meBtn.onclick = () => this.center(atlas.localX, atlas.localZ);
    bar.appendChild(meBtn);
    const fitBtn = document.createElement('button');
    fitBtn.textContent = 'whole world';
    fitBtn.style.cssText = meBtn.style.cssText;
    fitBtn.onclick = () => { this.fitAll(); this.dirty = true; };
    bar.appendChild(fitBtn);
    // Weather overlay toggle (meteo field layers — temperature / weather type /
    // relative humidity). One active at a time; Off hides the layer + legend.
    const wxGroup = document.createElement('div');
    wxGroup.style.cssText = 'display:flex;gap:2px;margin-left:6px';
    const wxDefs: Array<[MapMode, string, string]> = [
      ['none', 'layer off', 'Hide the layer'],
      ['temp', '🌡 temp', 'Air temperature overlay (°C)'],
      ['weather', '🌦 weather', 'Weather type overlay (rain/snow/fog/cloud)'],
      ['humidity', '💧 humidity', 'Relative humidity overlay (%)'],
      ['civ', '⚑ territory', 'Civilization ownership heatmap (amber Aurelian · blue Borean)'],
      ['war', '⚔ war', 'War layer — light red: declared war · dark red: fighting NOW · red dots: battles (size = fighters; PULSING = live, go there)'],
      ['people', '👥 people', 'Population heatmap — where the versonas are right now (blue → red), and every traveler online as a green diamond'],
      ['economy', 'ⓒ economy', 'Economy heatmap — GOLD where the credits sit (treasuries, purses, town funds), TEAL where the goods sit (stock at market value); white-hot = both'],
    ];
    for (const [mode, label, title] of wxDefs) {
      const b = document.createElement('button');
      b.textContent = label;
      b.title = title;
      b.dataset.mapMode = mode;
      b.style.cssText = meBtn.style.cssText;
      b.onclick = () => this.setWxMode(mode);
      this.wxButtons.set(mode, b);
      wxGroup.appendChild(b);
    }
    // The trade layer STACKS on any mode: the roads, each town's market habit, and every caravan / sky convoy in transit.
    const tb = document.createElement('button');
    tb.textContent = '⇄ trade';
    tb.title = 'Trade routes — the road net (dim), market habits (a town → its market city, heavier with more trips), and LIVE flows: caravans on the road (amber) and freighters in the sky (cyan), each moving toward its buyer';
    tb.dataset.mapTrade = '1';
    tb.style.cssText = meBtn.style.cssText + ';margin-left:6px';
    tb.onclick = () => this.setTrade(!this.tradeOn);
    this.tradeButton = tb;
    wxGroup.appendChild(tb);
    // GUILDS stack on any mode too: each guild's claim (a disc) and its hall — smaller than a hamlet, named only close in.
    const gb = document.createElement('button');
    gb.textContent = '⚜ guilds';
    gb.title = 'Guilds — every guild\'s claim and hall: your guild (cyan), other player guilds (violet), the trade guilds of the towns (their trade\'s colour, dashed). A guild is its own small system — smaller than a hamlet, named when you zoom in.';
    gb.dataset.mapGuilds = '1';
    gb.style.cssText = meBtn.style.cssText;
    gb.onclick = () => this.setGuilds(!this.guildsOn);
    this.guildButton = gb;
    wxGroup.appendChild(gb);
    bar.appendChild(wxGroup);
    this.styleWxButtons();
    root.appendChild(bar);

    // The tiled raster renderer: a worker pool that owns every sampled layer (terrain, weather, conflict).
    if (!this.raster) {
      this.raster = new MapRaster(() => new Worker(new URL('../workers/map_raster_worker.ts', import.meta.url), { type: 'module' }), rasterPoolSize());
      this.raster.onTile = () => { this.dirty = true; };
      if (this.seed) this.raster.setWorld(this.seed, this.sea);
    }

    // Map + side panel
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;flex:1 1 auto;min-height:0';
    this.wrap = document.createElement('div');
    this.wrap.style.cssText = 'flex:1 1 auto;position:relative;overflow:hidden;background:var(--ax-bg);min-width:0';
    this.canvas = document.createElement('canvas');
    this.canvas.dataset.atlasMap = 'world'; // the probe reads the picture back through this
    this.canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;cursor:crosshair';
    this.wrap.appendChild(this.canvas);
    this.travelBanner = document.createElement('div');
    this.travelBanner.style.cssText =
      'position:absolute;top:8px;left:50%;transform:translateX(-50%);z-index:4;display:none;' +
      'background:color-mix(in srgb, var(--ax-bg3) 95%, transparent);border:1px solid var(--ax-accent);border-radius:var(--ax-radius);padding:5px 14px;' +
      'font:600 12px var(--ax-font);color:var(--ax-text);white-space:nowrap';
    this.travelBanner.textContent = '⇪ SHUTTLE — pick a city or town to travel there (10 ⓒ fare)';
    this.wrap.appendChild(this.travelBanner);
    this.tip = document.createElement('div');
    this.tip.style.cssText =
      'position:absolute;display:none;pointer-events:none;background:color-mix(in srgb, var(--ax-bg) 94%, transparent);border:1px solid var(--ax-line2);' +
      'border-radius:var(--ax-radius);padding:4px 8px;font:11px var(--ax-font);color:var(--ax-text);white-space:nowrap;z-index:3';
    this.wrap.appendChild(this.tip);
    row.appendChild(this.wrap);

    this.panel = document.createElement('div');
    this.panel.style.cssText = 'flex:0 0 340px;border-left:1px solid var(--ax-line);overflow:auto;padding:8px 10px;min-height:0';
    row.appendChild(this.panel);
    root.appendChild(row);
    this.win.body.appendChild(root);

    this.wireCanvas();
    new ResizeObserver(() => { this.resizeCanvas(); }).observe(this.wrap);
    this.resizeCanvas();
  }

  private startLoops(): void {
    if (this.wxMode === 'people') this.ensurePeopleSubs(); // stopLoops ended it when the window closed
    if (!this.raf) {
      const loop = (): void => {
        this.raf = this.win?.isOpen() ? requestAnimationFrame(loop) : 0;
        if (this.pulse) this.dirty = true; // a LIVE battle's dot pulses — redraw every frame while one is on the map
        if (this.dirty && this.win?.isOpen()) { this.dirty = false; this.draw(); }
      };
      this.raf = requestAnimationFrame(loop);
    }
    if (this.timer === null) {
      this.timer = window.setInterval(() => {
        if (!this.win?.isOpen()) { this.stopLoops(); return; }
        this.dirty = true; // live rows (wars, econ, versona positions) refresh
        this.renderPanel();
      }, 2500);
    }
  }

  private stopLoops(): void {
    if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
    this.endPeopleSubs(); // closed: the travelers' rows go with it (the layer re-subscribes when the map shows people again)
  }

  // ── View math ─────────────────────────────────────────────────────────────
  private cw(): number { return this.canvas?.clientWidth ?? 1; }
  private ch(): number { return this.canvas?.clientHeight ?? 1; }
  private sx(wx: number): number { return (wx - this.view.cx) * this.view.scale + this.cw() / 2; }
  private sz(wz: number): number { return (wz - this.view.cz) * this.view.scale + this.ch() / 2; }
  private wx(px: number): number { return (px - this.cw() / 2) / this.view.scale + this.view.cx; }
  private wz(py: number): number { return (py - this.ch() / 2) / this.view.scale + this.view.cz; }

  private fitAll(): void {
    if (this.sites.length === 0) return;
    let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
    for (const s of this.sites) {
      x0 = Math.min(x0, s.centerX); x1 = Math.max(x1, s.centerX);
      z0 = Math.min(z0, s.centerZ); z1 = Math.max(z1, s.centerZ);
    }
    this.view.cx = (x0 + x1) / 2;
    this.view.cz = (z0 + z1) / 2;
    this.view.scale = Math.min(MAX_MAP_SCALE, Math.max(0.00018, Math.min(this.cw() / Math.max(1, x1 - x0 + 30000), this.ch() / Math.max(1, z1 - z0 + 30000))));
    this.fitPending = false;
  }

  private center(x: number, z: number): void {
    this.view.cx = x; this.view.cz = z;
    if (this.view.scale < 0.004) this.view.scale = 0.012; // arrive at region zoom
    this.dirty = true;
  }

  private mapView(): MapView { return { cx: this.view.cx, cz: this.view.cz, scale: this.view.scale, W: this.cw(), H: this.ch() }; }

  /** Fly the map to a structure at building zoom and flash it (the Find tab's rows, a pinned card). */
  private flyTo(e: MapEntry): void {
    const span = Math.min(this.cw(), this.ch());
    const size = e.r !== undefined ? e.r * 2.6 : Math.max(e.w ?? 0, e.d ?? 0, 36) * 4;
    this.view.cx = e.x; this.view.cz = e.z;
    this.view.scale = Math.min(MAX_MAP_SCALE, Math.max(LABEL_ZOOM * 1.2, span / size));
    this.layer.flash(e);
    this.dirty = true;
  }

  /** Standing in the Commons — the only world this map draws (2026-09-25): N opens it in a personal world, the Hive or a
   *  forge world too, and `createWaypoint` stamps the SERVER's current world, so a ◆ there made a waypoint at Commons
   *  coordinates in the wrong world (the HUD then followed it "312 km ahead"). Waypoints and "you" are Commons-only here. */
  /** G5: this world's live events for the map (one small table; read per draw). */
  private eventMarkers(): EventMarker[] {
    const conn = atlas.conn;
    if (!conn) return [];
    try { return liveEventMarkers((conn.db as unknown as { livingEvent: { iter(): Iterable<never> } }).livingEvent.iter(), atlas.currentWorldId, Date.now(), LIVING_EVENT_KINDS); } catch { return []; /* pre-publish server */ }
  }

  private inCommons(): boolean {
    return atlas.currentWorldId !== null && atlas.commonsWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId;
  }

  private refuseOffCommons(): boolean {
    if (this.inCommons()) return false;
    bus.emit('atlas:toast', { text: 'This map is the Commons — set waypoints for it while you stand in the Commons', kind: 'info', ms: 3500 });
    return true;
  }

  /** A waypoint on a structure; the in-world indicator follows the newest waypoint, so this one. */
  private waypointOn(e: MapEntry): void {
    const conn = atlas.conn;
    if (!conn) return;
    if (this.refuseOffCommons()) return; // off the Commons the waypoint would be stamped with the wrong world (2026-09-25)
    const name = e.name.slice(0, 32);
    conn.reducers.createWaypoint({ name, posX: e.x, posZ: e.z })
      .then(() => bus.emit('atlas:toast', { text: `Waypoint “${name}” set — follow the marker`, kind: 'success', ms: 3500 }))
      .catch((err: Error) => bus.emit('atlas:toast', { text: `Waypoint refused: ${err.message}`, kind: 'error' }));
  }

  /** Open the side panel's Find tab (with words, when given). */
  private openFind(text?: string): void {
    if (!this.win?.isOpen()) { if (!this.ensureWorld()) return; this.ensureOpenSubs(); if (!this.win) this.build(); this.win!.setOpen(true); this.dirty = true; this.startLoops(); }
    void this.layer.ensure();
    this.tab = 'find';
    this.renderPanel();
    this.find?.focus(text);
  }

  /** A click on the map at canvas point (px, py) — a waypoint follows, a structure pins, a settlement selects. */
  private clickMap(px: number, py: number): string {
    const wp = this.inCommons() ? waypointAt(myWaypoints(atlas.conn), (x) => this.sx(x), (z) => this.sz(z), px, py, 11) : null; // this world's waypoints are not on the Commons map (2026-09-25)
    if (wp) { trackWaypoint(wp.waypointId); this.dirty = true; return `waypoint:${String(wp.waypointId)}`; }
    // G5: a live event's glyph sets (and follows) a waypoint to it.
    const evm = this.inCommons() ? eventMarkerAt(this.eventMarkers(), (x) => this.sx(x), (z) => this.sz(z), px, py, 13) : null;
    if (evm) { waypointToEvent(atlas.conn, evm); this.dirty = true; return `event:${evm.id}`; }
    const site = this.pick(px, py);
    const building = this.view.scale >= LABEL_ZOOM; // at building zoom the thing under the cursor wins over the town's dot
    const entry = site && !building ? null : this.layer.pick(this.mapView(), px, py);
    if (entry) {
      if (!this.find) this.openFind(); else { this.tab = 'find'; this.renderPanel(); }
      this.find?.pin(entry);
      this.dirty = true;
      return entry.id;
    }
    if (site) { this.select(site); return `site:${site.id}`; }
    return '';
  }

  private resizeCanvas(): void {
    const c = this.canvas;
    if (!c) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    c.width = Math.max(2, Math.floor(c.clientWidth * dpr));
    c.height = Math.max(2, Math.floor(c.clientHeight * dpr));
    this.dirty = true;
  }

  private wireCanvas(): void {
    const c = this.canvas!;
    let drag: { px: number; py: number; cx: number; cz: number } | null = null;
    c.addEventListener('pointerdown', (e) => {
      drag = { px: e.clientX, py: e.clientY, cx: this.view.cx, cz: this.view.cz };
      c.setPointerCapture(e.pointerId);
    });
    c.addEventListener('pointermove', (e) => {
      const r = c.getBoundingClientRect();
      if (drag) {
        this.view.cx = drag.cx - (e.clientX - drag.px) / this.view.scale;
        this.view.cz = drag.cz - (e.clientY - drag.py) / this.view.scale;
        this.dirty = true;
        return;
      }
      const px = e.clientX - r.left, py = e.clientY - r.top;
      const siteHit = this.pick(px, py);
      // BUILDING ZOOM: the structure under the cursor wins over the town's dot; below it, the dot wins.
      const entry = siteHit && this.view.scale < LABEL_ZOOM ? null : this.layer.pick(this.mapView(), px, py);
      const hit = entry ? null : siteHit;
      if (entry !== this.hoverEntry) { this.hoverEntry = entry; this.dirty = true; }
      if (hit !== this.hovered) { this.hovered = hit; this.dirty = true; }
      if (this.tip && entry) {
        this.tip.style.display = 'block';
        this.tip.style.left = `${px + 14}px`;
        this.tip.style.top = `${py + 8}px`;
        if (entry !== this.tipEntry) { this.tipEntry = entry; this.tipFor = null; this.tip.innerHTML = this.layer.cardHtml(entry, atlas.localX, atlas.localZ); }
        return;
      }
      this.tipEntry = null;
      if (this.tip) {
        if (hit) {
          this.tip.style.display = 'block';
          this.tip.style.left = `${e.clientX - r.left + 14}px`;
          this.tip.style.top = `${e.clientY - r.top + 8}px`;
          // The population count spreads the whole npc_agent table (~6,000
          // rows): only when the hovered site changes, not per pointer event (2026-09-12).
          if (hit !== this.tipFor) {
            this.tipFor = hit;
            const civ = this.civOf(hit);
            const pop = this.tbl<AgentRow>('npcAgent').filter((a) => Number(a.settlementId) === dbSid(hit.id)).length;
            let extra = '';
            if (this.wxMode === 'economy') { // the layer's own numbers for the town under the cursor
              const e = this.economyBySite().get(dbSid(hit.id));
              if (e) extra = `<br><span style="color:var(--ax-hi)">ⓒ${Math.round(e.credits).toLocaleString()} in credits</span> · <span style="color:var(--ax-hi)">goods worth ⓒ${Math.round(e.resources).toLocaleString()}</span>`;
            }
            this.tip.innerHTML =
              `<b>${esc(this.nameOf(hit))}</b> <span style="opacity:.7">${hit.tier}</span><br>` +
              `<span style="color:${CIV_COLOR[civ]}">${CIV_TITLE[civ]}</span>` +
              `<span style="opacity:.7"> · ${esc(pretty(hit.biome))}${pop ? ` · ${pop} folk` : ''}</span>` + extra;
          }
        } else { this.tip.style.display = 'none'; this.tipFor = null; }
      }
    });
    const endDrag = (e: PointerEvent): void => {
      if (!drag) return;
      const moved = Math.hypot(e.clientX - drag.px, e.clientY - drag.py);
      drag = null;
      if (moved < 5) {
        const r = c.getBoundingClientRect();
        this.clickMap(e.clientX - r.left, e.clientY - r.top);
      }
    };
    c.addEventListener('pointerup', endDrag);
    c.addEventListener('pointerleave', () => {
      if (this.tip) this.tip.style.display = 'none';
      this.tipFor = null; this.tipEntry = null;
      if (this.hoverEntry || this.hovered) { this.hoverEntry = null; this.hovered = null; this.dirty = true; }
    });
    // Right-click: edit the waypoint under the cursor, or set a new one here.
    c.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      const conn = atlas.conn;
      if (!conn) return;
      if (this.refuseOffCommons()) return; // a right-click waypoint off the Commons landed in the wrong world (2026-09-25)
      const r = c.getBoundingClientRect();
      const px = e.clientX - r.left, py = e.clientY - r.top;
      let hitW: ReturnType<typeof myWaypoints>[0] | null = null;
      let bestD = 12;
      for (const w of myWaypoints(conn)) {
        const d = Math.hypot(this.sx(w.posX) - px, this.sz(w.posZ) - py);
        if (d < bestD) { bestD = d; hitW = w; }
      }
      if (hitW) promptEditWaypoint(conn, hitW);
      else promptCreateWaypoint(conn, this.wx(px), this.wz(py));
      this.dirty = true;
    });
    c.addEventListener('wheel', (e) => {
      e.preventDefault();
      const r = c.getBoundingClientRect();
      const mx = e.clientX - r.left, my = e.clientY - r.top;
      const wxAt = this.wx(mx), wzAt = this.wz(my);
      const f = e.deltaY < 0 ? 1.22 : 1 / 1.22;
      this.view.scale = Math.min(MAX_MAP_SCALE, Math.max(0.00018, this.view.scale * f));
      // Keep the point under the cursor stationary.
      this.view.cx = wxAt - (mx - this.cw() / 2) / this.view.scale;
      this.view.cz = wzAt - (my - this.ch() / 2) / this.view.scale;
      this.dirty = true;
    }, { passive: false });
  }

  private pick(px: number, py: number): SettlementSite | null {
    let best: SettlementSite | null = null;
    let bestD = 13;
    for (const s of this.sites) {
      const d = Math.hypot(this.sx(s.centerX) - px, this.sz(s.centerZ) - py);
      if (!(TIER_R[s.tier] > 0)) continue; // districts are drawn as their capital
      const r = TIER_R[s.tier] + 5;
      if (d < r + 4 && d < bestD) { best = s; bestD = d; }
    }
    return best;
  }

  private select(s: SettlementSite): void {
    if (this.travelMode && (s.tier === 'city' || s.tier === 'town')) {
      void this.bookShuttle(s);
      return;
    }
    this.selected = s;
    this.tab = 'settlement';
    this.ensureSidSubs(BigInt(dbSid(s.id)));
    this.dirty = true;
    this.renderPanel();
  }

  // ── Layers (the tiled raster does the sampling — lib/map_raster.ts) ─────────
  private setWxMode(mode: MapMode): void {
    this.wxMode = mode;
    if (mode === 'people') this.ensurePeopleSubs(); else this.endPeopleSubs();
    if (mode === 'economy') this.ensureEconSubs();
    this.styleWxButtons();
    this.dirty = true;
  }

  private setGuilds(on: boolean): void {
    this.guildsOn = on;
    this.styleWxButtons();
    this.dirty = true;
  }

  private setTrade(on: boolean): void {
    this.tradeOn = on;
    if (on) this.ensureTradeSubs();
    this.styleWxButtons();
    this.dirty = true;
  }

  private styleWxButtons(): void {
    const style = (b: HTMLButtonElement, on: boolean): void => {
      b.style.background = on ? 'var(--ax-sel)' : 'var(--ax-bg3)';
      b.style.borderColor = on ? 'var(--ax-accent)' : 'var(--ax-line2)';
    };
    for (const [mode, b] of this.wxButtons) style(b, mode === this.wxMode);
    if (this.tradeButton) style(this.tradeButton, this.tradeOn);
    if (this.guildButton) style(this.guildButton, this.guildsOn);
  }

  /** People: every traveler's row (the pad is a ghost pile — `presence.online` decides who is HERE) and the versonas' hot positions. */
  private ensurePeopleSubs(): void {
    if (this.peopleSubbed) return;
    this.peopleSubbed = true;
    // ONE held handle, released with the layer (2026-09-25): `sub()` dropped it, so each open leaked a whole-table query.
    try { this.peopleAgentHandle = atlas.conn?.subscriptionBuilder().subscribe(['SELECT * FROM npc_agent']) ?? null; } catch { /* pre-publish server */ }
    try { this.peopleSubHandle = atlas.conn?.subscriptionBuilder().subscribe(['SELECT * FROM player_state']) ?? null; } catch { /* pre-publish server */ }
  }

  /** The people layer is off (another layer, or the window closed): its whole-table `player_state` rows go. */
  private endPeopleSubs(): void {
    if (!this.peopleSubbed) return;
    this.peopleSubbed = false;
    try { this.peopleSubHandle?.unsubscribe(); } catch { /* already ended */ }
    this.peopleSubHandle = null;
    try { this.peopleAgentHandle?.unsubscribe(); } catch { /* already ended */ } // the npc_agent query goes with its layer (2026-09-25 leak fix)
    this.peopleAgentHandle = null;
  }

  /** Economy: the purses (npc_agent.wallet) and the town books (function_ledger) beside the econ mirror the window already holds. */
  private ensureEconSubs(): void {
    if (this.econSubbed) return;
    this.econSubbed = true;
    this.sub(['SELECT * FROM npc_agent', 'SELECT * FROM function_ledger']);
  }

  /** Trade: the ground caravans, the sky convoys, and every town's market habit. */
  private ensureTradeSubs(): void {
    if (this.tradeSubbed) return;
    this.tradeSubbed = true;
    this.sub(['SELECT * FROM caravan', 'SELECT * FROM cargo_convoy', 'SELECT * FROM market_link']);
  }

  private rasterView(): { cx: number; cz: number; W: number; H: number; scale: number } {
    return { cx: this.view.cx, cz: this.view.cz, W: this.cw(), H: this.ch(), scale: this.view.scale };
  }

  /** The active sampled layer over the terrain (the weather field on a 60 s bucket; the conflict layers on a row snapshot). */
  private drawRasterLayer(g: CanvasRenderingContext2D): void {
    const r = this.raster;
    if (!r || !RASTER_MODES.has(this.wxMode)) return;
    const mode = this.wxMode as 'temp' | 'weather' | 'humidity' | 'civ' | 'war';
    if (mode === 'civ' || mode === 'war') {
      const now = performance.now();
      if (!this.memo.conflict.v || now - this.memo.conflict.at > 2000) { // the rows are walked twice a second at most (npc_agent is thousands of rows)
        const snap = this.conflictInputs();
        // Each layer's digest names the rows IT is drawn from: the territory the settlements' civs, the war layer
        // the wars and clashes — a clash ending no longer wipes the territory (bug sweep 2026-09-23).
        const civ = `${this.civGen}|${snap.sites.map((s) => s.civ).join('')}`;
        const war = `${snap.warSites.map((w) => `${w.x | 0},${w.z | 0}`).join(';')}|${snap.clashes.map((c) => `${c.x | 0},${c.z | 0}`).join(';')}`;
        this.memo.conflict = { at: now, v: snap, digest: `${civ}#${war}`, civ, war };
      }
      r.setSnapshot(this.memo.conflict.v!, { civ: this.memo.conflict.civ, war: this.memo.conflict.war });
      r.draw(g, mode, this.rasterView(), 1);
      return;
    }
    // The time bucket keys a 60 s refresh — fronts advect at ~11 m/s, so the layer redraws itself as the weather systems move.
    // Every tile of a bucket samples ONE instant (the bucket's start, one time of day): a tile computed 40 s into the
    // minute stood ~440 m of drift from its neighbour, and the fronts stepped at tile borders (bug sweep 2026-09-23).
    const bucket = Math.floor(Date.now() / 60000);
    if (this.wxClock.bucket !== bucket) this.wxClock = { bucket, tod: currentTod() };
    r.draw(g, mode, this.rasterView(), 1, { bucket, tSec: bucket * 60, tod: this.wxClock.tod });
  }

  // ── People + economy heat (lib/map_heat.ts — blobs and a palette, no sampling) ──
  private peoplePoints(): { versonas: HeatPoint[]; players: Array<{ x: number; z: number; name: string }> } {
    const now = performance.now();
    if (this.memo.people.v && now - this.memo.people.at < 1000) return this.memo.people.v;
    const versonas: HeatPoint[] = [];
    for (const v of this.tbl<VersonaRow>('versona')) {
      if (v.curX === 0 && v.curZ === 0) continue; // never placed
      versonas.push({ x: v.curX, z: v.curZ, v: 1 });
    }
    const online = new Set<string>();
    for (const p of this.tbl<PresenceRow>('presence')) if (p.online) online.add(p.identity.toHexString());
    const names = new Map<string, string>();
    for (const a of this.tbl<AccountRow>('account')) names.set(a.identity.toHexString(), a.username);
    const players: Array<{ x: number; z: number; name: string }> = [];
    const wid = atlas.commonsWorldId;
    for (const p of this.tbl<PlayerRow>('playerState')) {
      const hex = p.identity.toHexString();
      if (!online.has(hex)) continue; // the arrival pad is a ghost pile of every account ever made — only the online walk the map
      if (wid !== null && p.worldId !== wid) continue;
      players.push({ x: p.posX, z: p.posZ, name: names.get(hex) ?? hex.slice(0, 6) });
    }
    this.memo.people = { at: now, v: { versonas, players } };
    return { versonas, players };
  }

  /** A heat layer through the per-view cache: painted once per (view, data refresh), blitted on every other frame —
   *  12k points binned and a full-canvas getImageData → colorize → putImageData ran on EVERY frame while anything
   *  else on the map animated (bug sweep 2026-09-23). */
  private blitHeat(g: CanvasRenderingContext2D, W: number, H: number, key: string, composite: GlobalCompositeOperation,
    paint: (cg: CanvasRenderingContext2D, W: number, H: number, ox: number, oy: number) => void): void {
    // A PAN is a translation (2026-09-28, "the heat map repaints per pan frame"): heat is world-anchored and its bins
    // and blob sizes depend on the SCALE only, so the layer is painted once with a quarter-screen margin on every side
    // and a pan inside the margin blits the cached image shifted — exact. A zoom, a resize, new data or a pan past the
    // margin repaints (the full-canvas getImageData → colorize → putImageData ran on every pan frame before).
    const s = this.view.scale;
    const full = `${key}|${s}|${W}|${H}`;
    let hc = this.heatCache;
    const shiftX = hc ? (this.view.cx - hc.cx) * s + hc.mx : 0, shiftY = hc ? (this.view.cz - hc.cz) * s + hc.my : 0;
    if (!hc || hc.key !== full || shiftX < 0 || shiftY < 0 || shiftX > 2 * hc.mx || shiftY > 2 * hc.my) {
      const mx = Math.ceil(W / 4), my = Math.ceil(H / 4), W2 = W + 2 * mx, H2 = H + 2 * my;
      const c = hc?.canvas ?? document.createElement('canvas');
      if (c.width !== W2 || c.height !== H2) { c.width = W2; c.height = H2; }
      const cg = c.getContext('2d')!;
      cg.setTransform(1, 0, 0, 1, 0, 0);
      cg.clearRect(0, 0, W2, H2);
      paint(cg, W2, H2, mx, my);
      hc = this.heatCache = { key: full, canvas: c, cx: this.view.cx, cz: this.view.cz, mx, my };
      this.census.heatPaints = (this.census.heatPaints ?? 0) + 1;
    }
    const ox = (this.view.cx - hc.cx) * s + hc.mx, oy = (this.view.cz - hc.cz) * s + hc.my;
    const prev = g.globalCompositeOperation;
    g.globalCompositeOperation = composite;
    g.drawImage(hc.canvas, ox, oy, W, H, 0, 0, W, H);
    g.globalCompositeOperation = prev;
  }

  private drawPeople(g: CanvasRenderingContext2D, W: number, H: number): void {
    const { versonas, players } = this.peoplePoints();
    this.blitHeat(g, W, H, `people|${this.memo.people.at}`, 'source-over', (cg, W2, H2, ox, oy) => {
      const to = (x: number, z: number): [number, number] => [this.sx(x) + ox, this.sz(z) + oy];
      const pts: HeatPoint[] = versonas.slice();
      for (const p of players) pts.push({ x: p.x, z: p.z, v: PLAYER_HEAT_WEIGHT });
      const bins = binHeat(pts, heatCellM(1 / this.view.scale));
      // The screen floor keeps a town's warmth wider than its marker at continental zoom (a city dot is ~10 px with its ring).
      drawHeat(cg, bins, W2, H2, this.view.scale, to, { radiusM: 320, minPx: 15, maxPx: 90, palette: PEOPLE_PALETTE });
    });
    // Every traveler online: a green diamond with their name — a person you can go and meet.
    g.font = 'bold 10px system-ui';
    for (const p of players) {
      const x = this.sx(p.x), y = this.sz(p.z);
      if (x < -20 || y < -20 || x > W + 20 || y > H + 20) continue;
      g.fillStyle = '#7ef0a0'; g.strokeStyle = '#0a2a14'; g.lineWidth = 1.2;
      g.beginPath(); g.moveTo(x, y - 6); g.lineTo(x + 5, y); g.lineTo(x, y + 6); g.lineTo(x - 5, y); g.closePath(); g.fill(); g.stroke();
      g.fillStyle = 'rgba(8,12,20,.75)';
      const tw = g.measureText(p.name).width;
      g.fillRect(x + 7, y - 7, tw + 6, 13);
      g.fillStyle = '#c8ffd8';
      g.fillText(p.name, x + 10, y + 3);
    }
    this.census.versonas = versonas.length;
    this.census.players = players.length;
  }

  /** Per settlement: the credits (treasury + every resident's purse + the town's books) and the goods at market value. */
  private economyBySite(): Map<number, { credits: number; resources: number }> {
    const now = performance.now();
    if (this.memo.econ.v && now - this.memo.econ.at < 2000) return this.memo.econ.v;
    const out = new Map<number, { credits: number; resources: number }>();
    const at = (sid: number): { credits: number; resources: number } => { let e = out.get(sid); if (!e) { e = { credits: 0, resources: 0 }; out.set(sid, e); } return e; };
    for (const e of this.tbl<EconRow>('settlementEcon')) {
      const sid = Number(e.settlementId);
      if (!(sid > 0)) continue;
      const r = at(sid);
      r.credits += Number(e.treasury);
      // Prices are centi-credits per unit: stock × price / 100 = what the shelves would fetch.
      r.resources += (e.stockFood * e.priceFood + e.stockOre * e.priceOre + e.stockTools * e.priceTools + e.stockLuxury * e.priceLuxury) / 100;
    }
    for (const a of this.tbl<AgentRow>('npcAgent')) { const sid = Number(a.settlementId); if (sid > 0) at(sid).credits += Number(a.wallet); }
    for (const l of this.tbl<LedgerRow>('functionLedger')) { const sid = Number(l.settlementId); if (sid > 0) at(sid).credits += Number(l.cash); }
    this.memo.econ = { at: now, v: out };
    return out;
  }

  private drawEconomy(g: CanvasRenderingContext2D, W: number, H: number): void {
    const econ = this.economyBySite();
    const credits: HeatPoint[] = [], resources: HeatPoint[] = [];
    let cSum = 0, rSum = 0;
    for (const s of this.sites) {
      const e = econ.get(dbSid(s.id));
      if (!e) continue;
      if (e.credits > 0) { credits.push({ x: s.centerX, z: s.centerZ, v: e.credits }); cSum += e.credits; }
      if (e.resources > 0) { resources.push({ x: s.centerX, z: s.centerZ, v: e.resources }); rSum += e.resources; }
    }
    // Two channels composited additively: a town rich in both reads white-hot.
    this.blitHeat(g, W, H, `econ|${this.memo.econ.at}`, 'lighter', (cg, W2, H2, ox, oy) => {
      const to = (x: number, z: number): [number, number] => [this.sx(x) + ox, this.sz(z) + oy];
      drawHeat(cg, credits, W2, H2, this.view.scale, to, { radiusM: 900, minPx: 17, maxPx: 120, palette: CREDITS_PALETTE }, 'lighter');
      drawHeat(cg, resources, W2, H2, this.view.scale, to, { radiusM: 900, minPx: 17, maxPx: 120, palette: RESOURCES_PALETTE }, 'lighter');
    });
    this.census.credits = Math.round(cSum);
    this.census.resources = Math.round(rSum);
  }

  // ── Trade (lib/trade_map.ts) ──────────────────────────────────────────────
  private tradeRows(): { flows: Flow[]; links: LinkLine[] } {
    const bySid = new Map<number, { x: number; z: number }>();
    for (const s of this.sites) bySid.set(dbSid(s.id), { x: s.centerX, z: s.centerZ });
    const site = (sid: number): { x: number; z: number } | undefined => bySid.get(sid);
    const now = performance.now();
    if (this.memo.trade.rows && this.memo.trade.links && now - this.memo.trade.at < 1000) {
      return { flows: liveFlows(this.memo.trade.rows, site, BigInt(Date.now()) * 1000n), links: this.memo.trade.links }; // the marks move every frame; the rows are read once a second
    }
    const rows: FlowRow[] = [];
    // The accessor is the schema EXPORT's name (`caravanRow`), not the table's ('caravan') — a wrong name reads [] silently.
    for (const c of this.tbl<CaravanRow>('caravanRow')) rows.push({ fromSid: Number(c.fromSid), toSid: Number(c.toSid), commodity: c.commodity, qty: c.qty, departMicros: c.departAt.microsSinceUnixEpoch, arriveMicros: c.arriveAt.microsSinceUnixEpoch, status: c.status, kind: 'caravan' });
    for (const c of this.tbl<ConvoyRow>('cargoConvoy')) rows.push({ fromSid: Number(c.fromSid), toSid: Number(c.toSid), commodity: c.commodity, qty: c.qty, departMicros: c.departAt.microsSinceUnixEpoch, arriveMicros: c.arriveAt.microsSinceUnixEpoch, status: c.status, kind: 'convoy', fromX: c.fromX, fromZ: c.fromZ, toX: c.toX, toZ: c.toZ });
    const nowMicros = BigInt(Date.now()) * 1000n;
    const flows = liveFlows(rows, site, nowMicros);
    const links = marketLinkLines(this.tbl<MarketLinkRow>('marketLink').map((l) => ({ settlementId: Number(l.settlementId), citySid: Number(l.citySid), trips: l.trips })), site);
    this.memo.trade = { at: now, rows, links };
    return { flows, links };
  }

  private drawTrade(g: CanvasRenderingContext2D, W: number, H: number): void {
    const seed = this.seed;
    if (!seed) return;
    const inView = (x: number, y: number, m = 40): boolean => x > -m && y > -m && x < W + m && y < H + m;
    // The roads — the substrate every flow rides on. Thin, dim; one path, one stroke.
    const segs = routeSegments(seed);
    g.strokeStyle = 'rgba(214,190,150,.32)';
    g.lineWidth = 1;
    g.beginPath();
    let roads = 0;
    for (const s of segs) {
      const ax = this.sx(s.ax), ay = this.sz(s.az), bx = this.sx(s.bx), by = this.sz(s.bz);
      if (!inView(ax, ay, 200) && !inView(bx, by, 200)) continue;
      g.moveTo(ax, ay); g.lineTo(bx, by); roads++;
    }
    g.stroke();
    const { flows, links } = this.tradeRows();
    // Market habits: a town's line to its market city, heavier with more trips, an arrowhead at the city.
    g.strokeStyle = 'rgba(232,196,106,.55)';
    g.fillStyle = 'rgba(232,196,106,.8)';
    for (const l of links) {
      const ax = this.sx(l.ax), ay = this.sz(l.az), bx = this.sx(l.bx), by = this.sz(l.bz);
      if (!inView(ax, ay, 200) && !inView(bx, by, 200)) continue;
      g.lineWidth = l.weight;
      g.beginPath(); g.moveTo(ax, ay); g.lineTo(bx, by); g.stroke();
      const an = Math.atan2(by - ay, bx - ax), L = Math.hypot(bx - ax, by - ay);
      if (L > 18) {
        const hx = bx - Math.cos(an) * 9, hy = by - Math.sin(an) * 9; // the head sits just short of the city's dot
        g.beginPath();
        g.moveTo(hx + Math.cos(an) * 5, hy + Math.sin(an) * 5);
        g.lineTo(hx + Math.cos(an + 2.5) * 5, hy + Math.sin(an + 2.5) * 5);
        g.lineTo(hx + Math.cos(an - 2.5) * 5, hy + Math.sin(an - 2.5) * 5);
        g.closePath(); g.fill();
      }
    }
    // Live flows: the leg as an animated dashed line (the dash marches toward the buyer), the hold as a moving mark.
    const tNow = performance.now() / 1000;
    let flowsOnScreen = 0;
    for (const f of flows) {
      const ax = this.sx(f.ax), ay = this.sz(f.az), bx = this.sx(f.bx), by = this.sz(f.bz);
      if (!inView(ax, ay, 300) && !inView(bx, by, 300)) continue;
      flowsOnScreen++;
      const sky = f.kind === 'convoy';
      g.strokeStyle = sky ? 'rgba(120,220,255,.7)' : 'rgba(255,190,90,.8)';
      g.lineWidth = sky ? 1.2 : 1.6;
      g.setLineDash(sky ? [3, 7] : [6, 6]);
      g.lineDashOffset = -((tNow * 26) % 12);
      g.beginPath(); g.moveTo(ax, ay); g.lineTo(bx, by); g.stroke();
      g.setLineDash([]);
      const p = alongLeg(f, f.t);
      const x = this.sx(p.x), y = this.sz(p.z);
      if (!inView(x, y)) continue;
      g.fillStyle = sky ? '#9be5ff' : '#ffc46a';
      g.strokeStyle = '#0a0f18';
      g.lineWidth = 1;
      if (sky) { // a freighter: a small chevron pointing down the leg
        const an = Math.atan2(by - ay, bx - ax);
        g.beginPath();
        g.moveTo(x + Math.cos(an) * 6, y + Math.sin(an) * 6);
        g.lineTo(x + Math.cos(an + 2.4) * 5, y + Math.sin(an + 2.4) * 5);
        g.lineTo(x + Math.cos(an - 2.4) * 5, y + Math.sin(an - 2.4) * 5);
        g.closePath(); g.fill(); g.stroke();
      } else { // a caravan: a rounded crate
        g.beginPath(); g.roundRect(x - 4, y - 3.5, 8, 7, 2); g.fill(); g.stroke();
      }
      if (this.view.scale > 0.0025) { // the manifest reads at region zoom
        g.font = '10px system-ui';
        const label = `${f.qty} ${pretty(f.commodity)}`;
        g.fillStyle = 'rgba(8,12,20,.75)';
        g.fillRect(x + 7, y - 7, g.measureText(label).width + 6, 13);
        g.fillStyle = sky ? '#d6f4ff' : '#ffe6bd';
        g.fillText(label, x + 10, y + 3);
      }
    }
    this.census.flows = flows.length;
    this.census.links = links.length;
    this.census.roads = roads;
    // The dashes march and the marks move — but only a flow ON SCREEN needs the next frame (any flow anywhere
    // kept the whole map redrawing every rAF frame — bug sweep 2026-09-23).
    if (flowsOnScreen > 0) this.pulse = true;
  }

  /** Snapshot the conflict-layer inputs (settlements + live wars/clashes). */
  private conflictInputs(): ConflictSnapshot {
    const sites = this.sites.map((s) => ({ x: s.centerX, z: s.centerZ, tier: s.tier, civ: this.civOf(s) }));
    const bySid = new Map<number, SettlementSite>();
    for (const s of this.sites) bySid.set(dbSid(s.id), s);
    const warSites: Array<{ x: number; z: number; tier: string }> = [];
    for (const w of this.tbl<WarRow>('warState')) {
      if (w.status !== 'declared') continue;
      for (const sid of [Number(w.aggressorSid), Number(w.defenderSid)]) {
        const s = bySid.get(sid);
        if (s) warSites.push({ x: s.centerX, z: s.centerZ, tier: s.tier });
      }
    }
    const nowMicros = BigInt(Date.now()) * 1000n;
    const clashes: Array<{ x: number; z: number }> = [];
    for (const c of this.tbl<{ x: number; z: number; endsAt: { microsSinceUnixEpoch: bigint } }>('warClash')) {
      if (c.endsAt.microsSinceUnixEpoch > nowMicros) clashes.push({ x: c.x, z: c.z });
    }
    // War rework 2026-09-16: an ENGAGED battle is "fighting NOW" too (the
    // war_clash row is the debug lever's scene; the pitched battle was invisible here).
    for (const b of this.tbl<{ phase: string; targetX: number; targetZ: number }>('battle')) {
      if (b.phase === 'engaged') clashes.push({ x: b.targetX, z: b.targetZ });
    }
    // Allied contributors: a town whose people hold a war duty is at war too
    // (npc_agent is the civ tab's lazy subscription — contributors show once it has loaded).
    const agentSid = new Map<string, number>();
    for (const a of this.tbl<{ versonaId: bigint; settlementId: bigint }>('npcAgent')) agentSid.set(a.versonaId.toString(), Number(a.settlementId));
    if (agentSid.size > 0) {
      const seen = new Set(warSites.map((s) => `${s.x}|${s.z}`));
      for (const w of this.tbl<{ versonaId: bigint }>('warAssignment')) {
        const sid = agentSid.get(w.versonaId.toString());
        const s = sid !== undefined ? bySid.get(sid) : undefined;
        if (s && !seen.has(`${s.centerX}|${s.centerZ}`)) { seen.add(`${s.centerX}|${s.centerZ}`); warSites.push({ x: s.centerX, z: s.centerZ, tier: s.tier }); }
      }
    }
    return { sites, warSites, clashes };
  }

  /** GUILDS: each claim as a disc (dashed for a town's trade guild), each hall as a small square — a hamlet's dot is a
   *  2.8 px radius, a guild's square reaches 1.4–1.6 px with its frame — and the name only once you are closer in than a hamlet's label (or the claim is
   *  14 px across). */
  private drawGuilds(g: CanvasRenderingContext2D, W: number, H: number): void {
    const conn = atlas.conn;
    if (!conn) return;
    const sc = this.view.scale;
    let drawn = 0, labels = 0, pl = 0, vs = 0;
    for (const c of claimsFor(conn, conn.identity ?? null)) {
      const x = this.sx(c.x), y = this.sz(c.z);
      const rp = c.r * sc;
      if (x < -rp - 40 || y < -rp - 40 || x > W + rp + 40 || y > H + rp + 40) continue;
      const hue = c.kind === 'versona' ? (TRADE_GUILDS[c.trade]?.hue ?? 45) : 0;
      const col = c.kind === 'player' ? (c.member ? '95,216,255' : '182,140,255') : '';
      const stroke = c.kind === 'player' ? `rgba(${col},.85)` : `hsla(${hue},60%,58%,.85)`;
      const fill = c.kind === 'player' ? `rgba(${col},.13)` : `hsla(${hue},60%,50%,.12)`;
      if (rp >= 4) {
        g.beginPath(); g.arc(x, y, rp, 0, Math.PI * 2);
        g.fillStyle = fill; g.fill();
        g.setLineDash(c.kind === 'versona' ? [4, 3] : []);
        g.strokeStyle = stroke; g.lineWidth = c.member ? 2 : 1.2; g.stroke();
        g.setLineDash([]);
      }
      // SMALLER THAN A HAMLET (2026-09-25): the 2.4 + 1 px frame drew a 6.8 px square — wider and larger in area than a
      // hamlet's 5.6 px disc (+1 px stroke). The whole marker, frame included, now stays within 1.6 px of its centre.
      const s = c.kind === 'player' ? 1.1 : 0.9;
      g.fillStyle = 'rgba(6,10,18,.9)'; g.fillRect(x - s - 0.5, y - s - 0.5, 2 * s + 1, 2 * s + 1);
      g.fillStyle = stroke; g.fillRect(x - s, y - s, 2 * s, 2 * s);
      drawn++; if (c.kind === 'player') pl++; else vs++;
      if (sc > 0.12 || rp >= 14) {
        const label = `${c.name.replace(/^the /, '')}${c.tag ? ` <${c.tag}>` : ''}`;
        g.font = `600 10px ${themeColor('--ax-font')}`;
        g.textAlign = 'center';
        g.lineWidth = 3; g.strokeStyle = 'rgba(6,10,18,.85)'; g.strokeText(label, x, y - Math.max(rp, 6) - 4);
        g.fillStyle = stroke; g.fillText(label, x, y - Math.max(rp, 6) - 4);
        g.textAlign = 'left';
        labels++;
      }
    }
    this.census.guilds = drawn; this.census.guildLabels = labels; this.census.guildPlayer = pl; this.census.guildVersona = vs;
  }

  /** Legend for the active weather layer (bottom-left of the map). */
  private drawWxLegend(g: CanvasRenderingContext2D, H: number): void {
    let y = H - 68;
    if (this.guildsOn) { // the guild legend stacks above the others
      const x = 10, w = 372, h = 36;
      g.fillStyle = themeColor('--ax-bg'); g.strokeStyle = themeColor('--ax-line2'); // a legend box is chrome: the theme's
      g.beginPath(); g.roundRect(x, y, w, h, 6); g.fill(); g.stroke();
      g.font = `10px ${themeColor('--ax-font')}`; g.fillStyle = themeColor('--ax-text');
      g.fillText(`guilds — ${this.census.guildPlayer} player guild${this.census.guildPlayer === 1 ? '' : 's'} · ${this.census.guildVersona} trade guilds in view`, x + 8, y + 13);
      const sw = (cx: number, css: string, dash: boolean, text: string): void => {
        g.strokeStyle = css; g.lineWidth = 1.5; g.setLineDash(dash ? [4, 3] : []);
        g.beginPath(); g.arc(cx + 6, y + 23, 5, 0, Math.PI * 2); g.stroke(); g.setLineDash([]);
        g.fillStyle = themeColor('--ax-soft'); g.fillText(text, cx + 15, y + 27);
      };
      sw(x + 8, 'rgba(95,216,255,.9)', false, 'your guild');
      sw(x + 100, 'rgba(182,140,255,.9)', false, 'other guilds');
      sw(x + 200, 'hsla(45,60%,58%,.9)', true, 'trade guilds of the towns');
      y -= h + 6;
    }
    if (this.tradeOn) { // the trade legend sits above the layer's (both may show)
      const x = 10, w = 372, h = 36;
      g.fillStyle = themeColor('--ax-bg'); g.strokeStyle = themeColor('--ax-line2'); // a legend box is chrome: the theme's
      g.beginPath(); g.roundRect(x, y, w, h, 6); g.fill(); g.stroke();
      g.font = `10px ${themeColor('--ax-font')}`; g.fillStyle = themeColor('--ax-text');
      g.fillText(`trade — ${this.census.flows} in transit · ${this.census.links} market habits · the roads`, x + 8, y + 13);
      g.strokeStyle = 'rgba(232,196,106,.9)'; g.lineWidth = 2; g.beginPath(); g.moveTo(x + 8, y + 23); g.lineTo(x + 24, y + 23); g.stroke();
      g.fillStyle = themeColor('--ax-soft'); g.fillText('town → its market', x + 28, y + 27);
      g.fillStyle = '#ffc46a'; g.beginPath(); g.roundRect(x + 120, y + 19, 8, 7, 2); g.fill();
      g.fillStyle = themeColor('--ax-soft'); g.fillText('caravan (road)', x + 132, y + 27);
      g.fillStyle = '#9be5ff'; g.beginPath(); g.moveTo(x + 214, y + 22); g.lineTo(x + 206, y + 18); g.lineTo(x + 206, y + 26); g.closePath(); g.fill();
      g.fillStyle = themeColor('--ax-soft'); g.fillText('freighter (sky)', x + 218, y + 27);
      g.strokeStyle = 'rgba(214,190,150,.5)'; g.lineWidth = 1; g.beginPath(); g.moveTo(x + 300, y + 23); g.lineTo(x + 316, y + 23); g.stroke();
      g.fillStyle = themeColor('--ax-soft'); g.fillText('road', x + 320, y + 27);
      y -= h + 6;
    }
    if (this.wxMode === 'none') return;
    if (this.wxMode === 'people' || this.wxMode === 'economy') {
      const x = 10, w = 330, h = 36;
      g.fillStyle = themeColor('--ax-bg'); g.strokeStyle = themeColor('--ax-line2'); // a legend box is chrome: the theme's
      g.beginPath(); g.roundRect(x, y, w, h, 6); g.fill(); g.stroke();
      g.font = `10px ${themeColor('--ax-font')}`; g.fillStyle = themeColor('--ax-text');
      if (this.wxMode === 'people') {
        g.fillText(`people — ${this.census.versonas.toLocaleString()} versonas · ${this.census.players} traveler${this.census.players === 1 ? '' : 's'} online`, x + 8, y + 13);
        const bx = x + 8, bw = 120, by = y + 19, bh = 8;
        for (let i = 0; i < bw; i++) {
          const o = Math.round((i / (bw - 1)) * 255) * 4;
          g.fillStyle = `rgb(${PEOPLE_PALETTE[o]},${PEOPLE_PALETTE[o + 1]},${PEOPLE_PALETTE[o + 2]})`;
          g.fillRect(bx + i, by, 1, bh);
        }
        g.fillStyle = themeColor('--ax-soft'); g.fillText('few → many', bx + bw + 6, by + 8);
        g.fillStyle = '#7ef0a0'; g.beginPath(); g.moveTo(x + 214, y + 18); g.lineTo(x + 219, y + 23); g.lineTo(x + 214, y + 28); g.lineTo(x + 209, y + 23); g.closePath(); g.fill();
        g.fillStyle = themeColor('--ax-soft'); g.fillText('a traveler (online)', x + 224, y + 27);
      } else {
        g.fillText(`economy — ⓒ${this.census.credits.toLocaleString()} in credits · goods worth ⓒ${this.census.resources.toLocaleString()}`, x + 8, y + 13);
        g.fillStyle = 'rgb(220,160,40)'; g.fillRect(x + 8, y + 19, 8, 8);
        g.fillStyle = themeColor('--ax-soft'); g.fillText('credits (treasury · purses · books)', x + 19, y + 27);
        g.fillStyle = 'rgb(40,190,180)'; g.fillRect(x + 180, y + 19, 8, 8);
        g.fillStyle = themeColor('--ax-soft'); g.fillText('goods at market value · both = white', x + 191, y + 27);
      }
      return;
    }
    if (this.wxMode === 'civ' || this.wxMode === 'war') {
      const x = 10, w = 330, h = 36;
      g.fillStyle = themeColor('--ax-bg'); g.strokeStyle = themeColor('--ax-line2'); // gaps wave (2026-09-28): the last two legend boxes still wore the navy UI's literals
      g.beginPath(); g.roundRect(x, y, w, h, 6); g.fill(); g.stroke();
      g.font = `10px ${themeColor('--ax-font')}`;
      g.fillStyle = themeColor('--ax-text');
      if (this.wxMode === 'civ') {
        g.fillText('territorial ownership (influence of settled ground)', x + 8, y + 13);
        g.fillStyle = CIV_COLOR.a; g.fillRect(x + 8, y + 19, 8, 8);
        g.fillStyle = themeColor('--ax-soft'); g.fillText(CIV_TITLE.a, x + 19, y + 27);
        g.fillStyle = CIV_COLOR.b; g.fillRect(x + 130, y + 19, 8, 8);
        g.fillStyle = themeColor('--ax-soft'); g.fillText(`${CIV_TITLE.b} · pale = contested frontier`, x + 141, y + 27);
      } else {
        g.fillText('war (live)', x + 8, y + 13);
        g.fillStyle = 'rgb(235,110,100)'; g.fillRect(x + 8, y + 19, 8, 8);
        g.fillStyle = themeColor('--ax-soft'); g.fillText('declared war', x + 19, y + 27);
        g.fillStyle = 'rgb(150,12,18)'; g.fillRect(x + 100, y + 19, 8, 8);
        g.fillStyle = themeColor('--ax-soft'); g.fillText('versonas FIGHTING here right now — visit the dark ground', x + 111, y + 27);
      }
      return;
    }
    const x = 10, w = this.wxMode === 'weather' ? 330 : 190, h = 36; // sits above the scale bar
    g.fillStyle = themeColor('--ax-bg'); g.strokeStyle = themeColor('--ax-line2'); // a legend box is chrome: the theme's (gaps wave 2026-09-28)
    g.beginPath(); g.roundRect(x, y, w, h, 6); g.fill(); g.stroke();
    g.font = `10px ${themeColor('--ax-font')}`;
    g.fillStyle = themeColor('--ax-text');
    if (this.wxMode === 'weather') {
      g.fillText('weather', x + 8, y + 13);
      const classes: WeatherClass[] = ['clear', 'cloudy', 'fog', 'rain', 'storm', 'snow'];
      let cx = x + 8;
      for (const cls of classes) {
        const [r, gg, b] = WEATHER_CLASS_COLOR[cls];
        g.fillStyle = `rgb(${r},${gg},${b})`;
        g.fillRect(cx, y + 19, 8, 8);
        g.fillStyle = themeColor('--ax-soft');
        g.fillText(cls, cx + 10, y + 27);
        cx += 12 + g.measureText(cls).width + 6;
      }
      return;
    }
    const isTemp = this.wxMode === 'temp';
    g.fillText(isTemp ? 'air temperature (°C)' : 'relative humidity (%)', x + 8, y + 13);
    const bx = x + 8, bw = w - 16, by = y + 19, bh = 8;
    for (let i = 0; i < bw; i++) {
      const f = i / (bw - 1);
      const [r, gg, b] = isTemp ? tempColor(-25 + f * 65) : humidityColor(f);
      g.fillStyle = `rgb(${r},${gg},${b})`;
      g.fillRect(bx + i, by, 1, bh);
    }
    g.fillStyle = themeColor('--ax-soft');
    const labels = isTemp ? ['-25', '0', '20', '40'] : ['0', '50', '100'];
    const fracs = isTemp ? [0, 25 / 65, 45 / 65, 1] : [0, 0.5, 1];
    for (let i = 0; i < labels.length; i++) {
      const lx = bx + fracs[i] * bw - (i === labels.length - 1 ? g.measureText(labels[i]).width : 0);
      g.fillText(labels[i], lx, by + bh + 8);
    }
  }

  // ── Drawing ───────────────────────────────────────────────────────────────
  private draw(): void {
    const c = this.canvas;
    if (!c || !this.win?.isOpen()) return;
    const g = c.getContext('2d');
    if (!g) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (this.fitPending && this.cw() > 1) this.fitAll();
    const W = this.cw(), H = this.ch();
    if (W < 2 || H < 2) return; // rolled up to the title bar: isOpen() stays true, and nothing is visible to draw
    g.clearRect(0, 0, W, H);
    g.fillStyle = '#060a12';
    g.fillRect(0, 0, W, H);

    // The terrain underlay: cached world tiles from the worker pool, a coarser ancestor where a tile has not landed yet.
    this.raster?.draw(g, 'terrain', this.rasterView(), 0.85);
    // The active layer above the terrain, below the settlement markers.
    this.pulse = false;
    this.drawRasterLayer(g);
    if (this.wxMode === 'people') this.drawPeople(g, W, H);
    else if (this.wxMode === 'economy') this.drawEconomy(g, W, H);
    if (this.tradeOn) this.drawTrade(g, W, H);
    // BUILDING ZOOM: streets, footprints, cemetery precincts, player structures and player-city borders.
    const mv = this.mapView();
    this.layer.drawBase(g, mv);
    if (this.guildsOn) this.drawGuilds(g, W, H);

    const wars = this.tbl<WarRow>('warState').filter((w) => w.status === 'declared');
    const atWar = new Set<number>();
    for (const w of wars) { atWar.add(Number(w.aggressorSid) - 1); atWar.add(Number(w.defenderSid) - 1); }

    // Versonas of the selected settlement — live positions.
    if (this.selected) {
      g.fillStyle = '#59e6c9';
      // (bug sweep) roster from npc_agent — versona.home_city_id is 0n for
      // every townsperson, so this dot layer was permanently empty.
      // The roster is read once a second through the settlement index, and each resident's position is ONE indexed
      // find — the hot row where the AOI box holds it, else the fat row (bug sweep 2026-09-23: two whole-table walks
      // with a BigInt→string per row, every frame).
      const selSid = dbSid(this.selected.id);
      const nowP = performance.now();
      if (!this.dotsMemo || this.dotsMemo.sid !== selSid || nowP - this.dotsMemo.at > 1000) {
        const ids: bigint[] = [];
        try {
          const db = atlas.conn?.db as unknown as { npcAgent: { npcAgentBySettlement: { filter(id: bigint): Iterable<AgentRow> } } };
          for (const a2 of db.npcAgent.npcAgentBySettlement.filter(BigInt(selSid))) ids.push(a2.versonaId);
        } catch { for (const a2 of this.tbl<AgentRow>('npcAgent')) if (Number(a2.settlementId) === selSid) ids.push(a2.versonaId); }
        this.dotsMemo = { sid: selSid, at: nowP, ids };
      }
      const db = atlas.conn?.db as unknown as { versonaState?: { versonaId: { find(id: bigint): { curX: number; curZ: number } | undefined } }; versona?: { versonaId: { find(id: bigint): VersonaRow | undefined } } } | undefined;
      for (const id of this.dotsMemo.ids) {
        const p = db?.versonaState?.versonaId.find(id) ?? db?.versona?.versonaId.find(id);
        if (!p) continue;
        g.fillRect(this.sx(p.curX) - 1.2, this.sz(p.curZ) - 1.2, 2.4, 2.4);
      }
    }

    // The Grand Circuit (phase 5): a thin amber loop, drawn once its samples exist on this thread.
    {
      const seedC = this.seed ?? '';
      const cc = seedC ? peekCircuit(seedC) : null;
      if (cc) {
        g.strokeStyle = 'rgba(255,190,90,.75)';
        g.lineWidth = 1.5;
        g.beginPath();
        const step = Math.max(1, Math.floor(cc.pts.length / 300));
        for (let i = 0; i < cc.pts.length; i += step) { const q = cc.pts[i]; const x = this.sx(q.x), y = this.sz(q.z); if (i === 0) g.moveTo(x, y); else g.lineTo(x, y); }
        g.closePath(); g.stroke();
      }
    }
    const labelTier = this.view.scale > 0.05 ? 'hamlet' : this.view.scale > 0.008 ? 'town' : 'city';
    const show = (t: string): boolean =>
      t === 'city' || t === 'capital' || (t === 'town' && labelTier !== 'city') || labelTier === 'hamlet';
    const q = this.search;
    // At building zoom the settlements' names join the structure labels' de-clutter (and win it) instead of being
    // painted over by a building's name.
    const siteNames: Array<{ x: number; y: number; text: string; colour: string }> = [];
    for (const s of this.sites) {
      const x = this.sx(s.centerX), y = this.sz(s.centerZ);
      if (x < -60 || y < -60 || x > W + 60 || y > H + 60) continue;
      const civ = this.civOf(s);
      const r = TIER_R[s.tier];
      if (!(r > 0)) continue;
      const dim = q !== '' && !this.nameOf(s).toLowerCase().includes(q);
      // At building zoom the town's own dot steps back (its buildings are the picture now).
      g.globalAlpha = (dim ? 0.25 : 1) * (mv.scale >= LABEL_ZOOM ? 0.45 : 1);
      if (atWar.has(s.id)) {
        g.strokeStyle = '#e5534b';
        g.lineWidth = 2;
        g.beginPath(); g.arc(x, y, r + 3.5, 0, Math.PI * 2); g.stroke();
      }
      g.fillStyle = CIV_COLOR[civ];
      g.strokeStyle = '#0a0f18';
      g.lineWidth = 1;
      g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill(); g.stroke();
      if (s.tier === 'city' || s.tier === 'capital') { // cities read as ringed seats
        g.strokeStyle = CIV_COLOR[civ];
        g.beginPath(); g.arc(x, y, r + 2.5, 0, Math.PI * 2); g.stroke();
      }
      if (s.tier === 'capital') { // the civ's CAPITAL: a second ring (the megawall) and a four-point star
        g.lineWidth = 1.5;
        g.beginPath(); g.arc(x, y, r + 6, 0, Math.PI * 2); g.stroke();
        g.fillStyle = '#ffffff';
        g.beginPath();
        for (let i = 0; i < 8; i++) { const rr = i % 2 === 0 ? r * 0.78 : r * 0.28; const an = -Math.PI / 2 + (i * Math.PI) / 4; g.lineTo(x + Math.cos(an) * rr, y + Math.sin(an) * rr); }
        g.closePath(); g.fill();
        g.lineWidth = 1;
      }
      if (this.selected?.id === s.id || this.hovered?.id === s.id) {
        g.strokeStyle = '#ffffff';
        g.lineWidth = 1.4;
        g.beginPath(); g.arc(x, y, r + 5.5, 0, Math.PI * 2); g.stroke();
      }
      g.globalAlpha = dim ? 0.25 : 1;
      if (show(s.tier) && mv.scale >= LABEL_ZOOM) { if (!dim) siteNames.push({ x, y: y - r - 12, text: this.nameOf(s), colour: CIV_COLOR[civ] }); }
      else if (show(s.tier)) {
        g.font = s.tier === 'capital' ? 'bold 13px system-ui' : s.tier === 'city' ? 'bold 12px system-ui' : '10px system-ui';
        g.fillStyle = 'rgba(8,12,20,.75)';
        const name = this.nameOf(s);
        const tw = g.measureText(name).width;
        g.fillRect(x + r + 3, y - 7, tw + 6, 14);
        g.fillStyle = dim ? 'rgba(230,240,255,.4)' : '#e6f0ff';
        g.fillText(name, x + r + 6, y + 4);
      }
      g.globalAlpha = 1;
    }

    // PLAYER CITIES (2026-09-23): a banner-coloured square at each City Hall (the row is global), the
    // civilisation's colour when it has one, the name from town zoom in.
    {
      const conn = atlas.conn as unknown as { db?: { city?: { iter(): Iterable<{ cityId: bigint; name: string; centerX: number; centerZ: number; cityRank: number; radiusM: number; status: string }> }; playerCivMember?: { iter(): Iterable<{ civId: bigint; cityId: bigint }> }; playerCiv?: { civId: { find(id: bigint): { banner: string; status: string } | null | undefined } } } } | null;
      const cities = conn?.db?.city ? [...conn.db.city.iter()] : [];
      if (cities.length > 0) {
        const civOf = new Map<bigint, string>();
        if (conn?.db?.playerCivMember && conn.db.playerCiv) {
          for (const m of conn.db.playerCivMember.iter()) {
            const c = conn.db.playerCiv.civId.find(m.civId);
            if (c && c.status === 'formed') civOf.set(m.cityId, c.banner);
          }
        }
        for (const c of cities) {
          const x = this.sx(c.centerX), y = this.sz(c.centerZ);
          const r = 3 + c.cityRank * 0.8;
          g.globalAlpha = c.status === 'active' ? 1 : 0.55;
          g.fillStyle = civOf.get(c.cityId) ?? '#d9a441';
          g.strokeStyle = '#0a0f18';
          g.lineWidth = 1;
          g.fillRect(x - r, y - r, r * 2, r * 2); g.strokeRect(x - r, y - r, r * 2, r * 2);
          // At building zoom the name sits above the border and joins the labels' de-clutter (it was painted over
          // its own City Hall's name); below it, beside the square.
          if (mv.scale >= LABEL_ZOOM) siteNames.push({ x, y: y - Math.max(r + 6, c.radiusM * mv.scale) - 12, text: c.name, colour: civOf.get(c.cityId) ?? '#d9a441' });
          else if (labelTier !== 'city') {
            g.font = '10px system-ui';
            g.fillStyle = 'rgba(8,12,20,.75)';
            const tw = g.measureText(c.name).width;
            g.fillRect(x + r + 3, y - 7, tw + 6, 14);
            g.fillStyle = '#f3e7c9';
            g.fillText(c.name, x + r + 6, y + 4);
          }
          g.globalAlpha = 1;
        }
      }
    }
    this.drawDeposits(g);
    // BUILDING ZOOM: glyphs, names (de-cluttered), the Find tab's matches, the hover outline and a search hit's flash.
    if (this.layer.drawTop(g, mv, this.hoverEntry, siteNames)) this.pulse = true;

    // BATTLES (war rework, 2026-09-16): a red dot per battle, sized by the
    // fighters in it — PULSING while it is live (march / engaged: you can go
    // there), still once it has happened (battle_record, kept a week).
    // Only on the WAR layer (the user's call): the plain map stays clean.
    if (this.wxMode === 'war') {
      const tNow = performance.now() / 1000;
      const wid = atlas.commonsWorldId;
      // The battles are read once a second; only their PULSE is per frame (three whole tables walked every frame
      // for as long as a battle was live or fading, up to 15 minutes — bug sweep 2026-09-23).
      if (!this.warMemo || performance.now() - this.warMemo.at > 1000) {
        const units = new Map<string, number>();
        for (const u of this.tbl<{ battleId: bigint }>('battleUnit')) { const k = u.battleId.toString(); units.set(k, (units.get(k) ?? 0) + 1); }
        const live: Array<{ x: number; z: number; n: number }> = [];
        const liveIds = new Set<string>();
        for (const b of this.tbl<{ battleId: bigint; worldId: bigint; phase: string; targetX: number; targetZ: number }>('battle')) {
          if (wid !== null && b.worldId !== wid) continue;
          if (b.phase === 'resolved') continue; // its record draws it
          liveIds.add(b.battleId.toString());
          live.push({ x: b.targetX, z: b.targetZ, n: units.get(b.battleId.toString()) ?? 2 });
        }
        const fought: Array<{ x: number; z: number; n: number; resolvedS: number }> = [];
        for (const r of this.tbl<{ battleId: bigint; worldId: bigint; x: number; z: number; units: number; resolvedAt: { microsSinceUnixEpoch: bigint } }>('battleRecord')) {
          if (wid !== null && r.worldId !== wid) continue;
          if (liveIds.has(r.battleId.toString())) continue;
          fought.push({ x: r.x, z: r.z, n: r.units, resolvedS: Number(r.resolvedAt.microsSinceUnixEpoch) / 1e6 });
        }
        this.warMemo = { at: performance.now(), live, fought };
      }
      const radiusFor = (n: number): number => Math.min(13, 3 + n * 0.55);
      const dot = (x: number, y: number, n: number, live: boolean): void => {
        if (x < -30 || y < -30 || x > W + 30 || y > H + 30) return;
        const base = radiusFor(n);
        const r = live ? base * (1 + 0.28 * Math.sin(tNow * 5)) : base;
        if (live) {
          g.fillStyle = 'rgba(229,83,75,0.22)';
          g.beginPath(); g.arc(x, y, r * 1.9 + 2, 0, Math.PI * 2); g.fill();
        }
        g.fillStyle = live ? '#ff5a4e' : '#b8362f';
        g.strokeStyle = '#1a0806';
        g.lineWidth = 1;
        g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill(); g.stroke();
      };
      let anyLive = false, anyFading = false;
      const RECORD_FADE_S = 120; // the last two minutes of a fought battle's dot fade out
      for (const b of this.warMemo.live) {
        dot(this.sx(b.x), this.sz(b.z), b.n, true);
        anyLive = true;
      }
      for (const r of this.warMemo.fought) {
        // THE MAP FORGETS IN A QUARTER HOUR (war tweaks 2026-09-17): a fought battle's dot fades over its last two minutes and is gone at RECORD_MAP_S — the record row itself lives a week for the chronicle.
        const age = Date.now() / 1000 - r.resolvedS;
        if (age > BATTLE.RECORD_MAP_S) continue;
        const fade = Math.max(0, Math.min(1, (BATTLE.RECORD_MAP_S - age) / RECORD_FADE_S));
        if (fade < 1) { g.globalAlpha = fade; anyFading = true; }
        dot(this.sx(r.x), this.sz(r.z), r.n, false);
        g.globalAlpha = 1;
      }
      if (anyLive || anyFading) this.pulse = true;
    }

    // Personal waypoints (own + THIS world only — the planet-aware filter). The one the in-world indicator follows is
    // ringed, with a dashed line from you to it.
    // Off the Commons, this world's waypoints and "you" do not belong on the Commons map (2026-09-25).
    const onMap = this.inCommons();
    const followed = onMap ? trackedWaypoint(atlas.conn) : null;
    if (followed && atlas.currentWorldId !== null) {
      g.save();
      g.setLineDash([7, 5]);
      g.strokeStyle = WAYPOINT_COLOR;
      g.globalAlpha = 0.8;
      g.lineWidth = 1.6;
      g.beginPath(); g.moveTo(this.sx(atlas.localX), this.sz(atlas.localZ)); g.lineTo(this.sx(followed.posX), this.sz(followed.posZ)); g.stroke();
      g.restore();
    }
    for (const w of onMap ? myWaypoints(atlas.conn) : []) {
      const x = this.sx(w.posX), y = this.sz(w.posZ);
      if (x < -80 || y < -80 || x > W + 80 || y > H + 80) continue;
      drawWaypointMarker(g, x, y, w.name, false, followed !== null && w.waypointId === followed.waypointId);
    }

    // G5: live events — the Commons' festivals, feasts and rites (a glyph + the title at town zoom; click = waypoint).
    for (const m of onMap ? this.eventMarkers() : []) {
      const x = this.sx(m.x), y = this.sz(m.z);
      if (x < -40 || y < -40 || x > W + 40 || y > H + 40) continue;
      g.fillStyle = 'rgba(8,12,20,.78)'; g.beginPath(); g.arc(x, y, 8, 0, Math.PI * 2); g.fill();
      g.strokeStyle = '#7ef0a0'; g.lineWidth = 1.4; g.stroke();
      g.font = '11px system-ui'; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = '#eef4ff';
      g.fillText(m.glyph, x, y + 0.5);
      g.textAlign = 'left'; g.textBaseline = 'alphabetic';
      if (this.view.scale >= LABEL_ZOOM * 0.5) {
        g.font = '10px system-ui';
        const t = `${m.title} · ${m.minutesLeft} min`, tw = g.measureText(t).width + 10;
        g.fillStyle = 'rgba(8,12,20,.78)'; g.fillRect(x - tw / 2, y + 10, tw, 14);
        g.fillStyle = '#7ef0a0'; g.fillText(t, x - tw / 2 + 5, y + 21);
      }
    }

    // The player.
    if (onMap) {
      const x = this.sx(atlas.localX), y = this.sz(atlas.localZ);
      if (x > -20 && y > -20 && x < W + 20 && y < H + 20) {
        g.fillStyle = '#7ef0a0';
        g.beginPath();
        g.moveTo(x, y - 6); g.lineTo(x + 5, y + 5); g.lineTo(x - 5, y + 5); g.closePath();
        g.fill();
        g.font = '10px system-ui';
        g.fillText('you', x + 8, y + 4);
      }
    }

    this.drawWxLegend(g, H);

    // Scale bar.
    const meters = 100 / this.view.scale;
    const nice = Math.pow(10, Math.floor(Math.log10(meters)));
    const barM = meters / nice >= 5 ? nice * 5 : meters / nice >= 2 ? nice * 2 : nice;
    const barPx = barM * this.view.scale;
    g.fillStyle = 'rgba(230,240,255,.75)';
    g.fillRect(12, H - 18, barPx, 2);
    g.font = '10px system-ui';
    g.fillText(barM >= 1000 ? `${barM / 1000} km` : `${barM} m`, 12, H - 24);
  }

  // ── Panel ─────────────────────────────────────────────────────────────────
  private renderPanel(): void {
    const p = this.panel;
    if (!p || !this.win?.isOpen()) return;
    const tabs =
      `<div style="display:flex;gap:6px;margin-bottom:8px">` +
      `<button data-tab="settlement" style="${this.tabCss(this.tab === 'settlement')}">Settlement</button>` +
      `<button data-tab="civ" style="${this.tabCss(this.tab === 'civ')}">Civilizations</button>` +
      `<button data-tab="find" style="${this.tabCss(this.tab === 'find')}">Find</button></div>`;
    const wireTabs = (): void => {
      for (const b of Array.from(p.querySelectorAll<HTMLButtonElement>('[data-tab]'))) {
        b.addEventListener('click', () => {
          this.tab = b.dataset.tab as 'settlement' | 'civ' | 'find';
          if (this.tab === 'civ') this.ensureCivTabSubs();
          if (this.tab === 'find') void this.layer.ensure();
          this.renderPanel();
          if (this.tab === 'find') this.find?.focus();
        });
      }
    };
    if (this.tab === 'find') {
      // The Find tab's DOM is built once and KEPT (a re-render would drop the words being typed); it re-queries itself.
      if (!this.find) {
        this.find = new FindPanel({
          entries: () => this.layer.entries(),
          status: () => (this.layer.status === 'ready' ? '' : this.layer.status === 'missing' ? 'buildings not baked for this world' : 'loading the building map…'),
          me: () => ({ x: atlas.localX, z: atlas.localZ }),
          viewBounds: () => ({ x0: this.wx(0), z0: this.wz(0), x1: this.wx(this.cw()), z1: this.wz(this.ch()) }),
          fly: (e) => this.flyTo(e),
          waypoint: (e) => this.waypointOn(e),
          showMatches: (m) => { this.layer.setMatches(m); this.dirty = true; },
        });
      }
      if (this.panelMode !== 'find') {
        p.innerHTML = tabs;
        p.appendChild(this.find.root);
        wireTabs();
        this.panelMode = 'find';
        this.find.refresh(true);
      } else this.find.refresh(false);
      return;
    }
    if (this.panelMode === 'find') this.layer.setMatches(null); // the matches belong to the Find tab
    this.panelMode = this.tab;
    p.innerHTML = tabs + (this.tab === 'civ' ? this.civHtml() : this.settlementHtml());
    wireTabs();
    for (const b of Array.from(p.querySelectorAll<HTMLButtonElement>('[data-goto]'))) {
      b.addEventListener('click', () => {
        const s = this.siteById(Number(b.dataset.goto));
        if (s) { this.select(s); this.center(s.centerX, s.centerZ); }
      });
    }
  }

  private tabCss(on: boolean): string {
    return `flex:1;padding:4px 0;border-radius:var(--ax-radius);cursor:pointer;border:1px solid ${on ? 'var(--ax-accent)' : 'var(--ax-line)'};` +
      `background:${on ? 'color-mix(in srgb, var(--ax-accent) 25%, transparent)' : 'transparent'};color:${on ? 'var(--ax-text)' : 'var(--ax-soft)'};font:600 11px var(--ax-font)`;
  }

  private h(title: string): string {
    return `<div style="font:700 11px var(--ax-font);letter-spacing:.08em;color:var(--ax-hi);margin:10px 0 4px;text-transform:uppercase">${title}</div>`;
  }

  private kv(k: string, v: string): string {
    return `<div style="display:flex;justify-content:space-between;gap:10px;padding:1.5px 0;border-bottom:1px solid var(--ax-line)">` +
      `<span style="opacity:.65">${k}</span><span style="text-align:right">${v}</span></div>`;
  }

  private bar(v: number, color: string): string {
    return `<span style="display:inline-block;width:44px;height:6px;background:var(--ax-bg3);border-radius:var(--ax-radius);vertical-align:1px">` +
      `<span style="display:block;width:${Math.min(100, Math.max(0, v))}%;height:100%;background:${color};border-radius:var(--ax-radius)"></span></span>`;
  }

  /** SOCIETY WAVE: the land's deposits, the service posts, the seven town scores, the seat in the Assembly. */
  private societyHtml(sid: number): string {
    const out: string[] = [];
    const nodes = this.tbl<NodeRow>('resourceNode').filter((n) => Number(n.settlementId) === sid);
    const deposits = nodes.filter((n) => n.good.startsWith('dep:'));
    const slots = this.tbl<SlotRow>('jobSlot').filter((j) => Number(j.settlementId) === sid && isServiceKey(j.recipeKey));
    const score = this.tbl<ScoreRow>('settlementScore').find((r) => Number(r.settlementId) === sid);
    const member = this.tbl<MemberRow>('federalMember').find((m) => Number(m.settlementId) === sid);
    const link = this.tbl<LinkRow>('marketLink').find((l) => Number(l.settlementId) === sid);
    const spec = this.tbl<SpecRow>('citySpec').find((c) => Number(c.settlementId) === sid);
    if (deposits.length + slots.length === 0 && !score && !member && !link && !spec) return '';
    out.push(this.h('land & society'));
    if (deposits.length > 0) {
      out.push(this.kv('deposits', deposits.map((d) => `<span style="color:var(--ax-hi)">◆</span> ${esc(pretty(d.good.slice(4)))} <span style="opacity:.5">${d.richness}</span>`).join(' · ')));
    }
    if (spec) out.push(this.kv('known for', esc(pretty(spec.spec))));
    if (link) {
      const city = this.siteById(Number(link.citySid) - 1);
      out.push(this.kv('trades at', city
        ? `<button data-goto="${city.id}" style="background:none;border:none;color:var(--ax-hi);text-decoration:underline;cursor:pointer;padding:0">${esc(this.nameOf(city))}</button> <span style="opacity:.5">(a habit, not an overlord)</span>`
        : `#${link.citySid}`));
    }
    if (slots.length > 0) {
      const held = slots.filter((j) => j.holderId !== 0n);
      const kinds = [...new Set(held.map((j) => pretty(j.recipeKey.slice(4))))];
      out.push(this.kv('service posts', `${held.length} of ${slots.length} staffed` + (kinds.length ? ` <span style="opacity:.55">— ${esc(kinds.slice(0, 6).join(', '))}${kinds.length > 6 ? '…' : ''}</span>` : '')));
    }
    if (score) {
      const keys: Array<[keyof ScoreRow, string]> = [['cohesion', '#7bc86c'], ['order', '#6cb2e8'], ['health', '#e57373'], ['learning', '#c9a0ff'], ['piety', '#e8c46a'], ['prosperity', '#e5a34b'], ['readiness', '#c0c8d4']];
      out.push(`<div style="display:grid;grid-template-columns:1fr 1fr;gap:1px 12px;padding:3px 0">` +
        keys.map(([k, col]) => `<span style="display:flex;justify-content:space-between;gap:6px"><span style="opacity:.65">${k}</span><span>${this.bar(Number(score[k]), col)} ${Math.round(Number(score[k]))}</span></span>`).join('') + `</div>`);
    }
    if (member) {
      const votes = TIER_VOTES[member.tier] ?? member.votes;
      const state = member.defiant ? '<span style="color:var(--ax-bad)">defiant — no votes while its war lasts</span>'
        : member.suspended ? `<span style="color:var(--ax-warn)">suspended — ${member.arrears} coins in arrears</span>` : 'in good standing';
      out.push(this.kv(`Assembly of civ ${esc(member.civ)}`, `${votes} bloc vote${votes === 1 ? '' : 's'} · ${state}`));
    }
    return out.join('');
  }

  /** The selected settlement's deposits, drawn where they lie (a gold diamond each). */
  private drawDeposits(g: CanvasRenderingContext2D): void {
    const s = this.selected;
    if (!s || this.view.scale < 0.02) return; // under ~50 m per pixel the diamonds would pile on the town's own dot
    const sid = s.id + 1;
    for (const n of this.tbl<NodeRow>('resourceNode')) {
      if (Number(n.settlementId) !== sid || !n.good.startsWith('dep:')) continue;
      const x = this.sx(n.x), y = this.sz(n.z);
      g.fillStyle = '#e8c46a'; g.strokeStyle = 'rgba(8,12,20,.85)'; g.lineWidth = 1;
      g.beginPath(); g.moveTo(x, y - 5); g.lineTo(x + 4, y); g.lineTo(x, y + 5); g.lineTo(x - 4, y); g.closePath(); g.fill(); g.stroke();
      if (this.view.scale >= 0.06) {
        g.font = '10px system-ui'; g.fillStyle = '#f3e2b0';
        g.fillText(n.good.slice(4).replace(/_/g, ' '), x + 7, y + 3);
      }
    }
  }

  private settlementHtml(): string {
    const s = this.selected;
    if (!s) return '<div style="opacity:.65;padding:8px 2px">Click a settlement on the map — every stat of the town opens here: its people, offices, laws, markets, wars, and dead.</div>';
    const sid = dbSid(s.id);
    const civ = this.civOf(s);
    const econ = this.econOf(s);
    const agents = this.tbl<AgentRow>('npcAgent').filter((a) => Number(a.settlementId) === sid);
    const agentIdSet = new Set(agents.map((a2) => a2.versonaId.toString())); // (bug sweep) home_city_id is universally 0
    const versonas = this.tbl<VersonaRow>('versona').filter((v) => agentIdSet.has(v.versonaId.toString()));
    const households = this.tbl<HouseholdRow>('household').filter((h2) => Number(h2.settlementId) === sid);
    const memberHouse = new Map<string, string>();
    if (households.length > 0) {
      const byId = new Map(households.map((h2) => [String(h2.householdId), h2.surname]));
      for (const m of this.tbl<HouseholdMemberRow>('householdMember')) {
        const sn = byId.get(String(m.householdId));
        if (sn) memberHouse.set(String(m.versonaId), sn);
      }
    }
    const faiths = new Map(this.tbl<FaithRow>('faithAdherence').map((f) => [String(f.versonaId), f.faithId]));
    const offices = this.tbl<OfficeRow>('settlementOffice').filter((o) => Number(o.settlementId) === sid);
    const graves = this.tbl<GraveRow>('grave').filter((g2) => Number(g2.settlementId) === sid);
    const slots = this.tbl<JobSlotRow>('jobSlot').filter((j) => Number(j.settlementId) === sid);
    const stock = this.tbl<StockRow>('settlementStock').filter((st) => Number(st.settlementId) === sid);
    const rounds = this.tbl<RoundRow>('marketRound').filter((r) => Number(r.settlementId) === sid);
    const clauses = this.tbl<ClauseRow>('charterClause').filter((cl) => Number(cl.settlementId) === sid);
    const norms = this.tbl<NormRow>('settlementNorm').filter((n) => Number(n.settlementId) === sid);
    const residents = this.tbl<ResidencyRow>('residency').filter((r) =>
      Number(r.settlementId) === sid && Number(r.paidUntil.microsSinceUnixEpoch / 1000n) > Date.now());
    const wars = this.warsOf(s.id);
    const rivals = this.tbl<RivalryRow>('settlementRivalry')
      .filter((r) => Number(r.aId) === sid || Number(r.bId) === sid)
      .sort((a, b) => b.grievance - a.grievance);
    const exchange = this.tbl<ExchangeRow>('exchangeOffice').some((x) => Number(x.settlementId) === sid);
    const agentById = new Map(agents.map((a) => [String(a.versonaId), a]));

    const html: string[] = [];
    const dist = Math.hypot(s.centerX - atlas.localX, s.centerZ - atlas.localZ);
    html.push(
      `<div style="font:700 16px var(--ax-font);color:var(--ax-text)">${esc(this.nameOf(s))}</div>` +
      `<div style="margin:1px 0 2px"><span style="color:${CIV_COLOR[civ]}">● ${CIV_TITLE[civ]}</span>` +
      `<span style="opacity:.7"> — ${s.tier} in the ${esc(pretty(s.biome))}</span></div>` +
      `<div style="opacity:.55;font-size:11px">(${Math.round(s.centerX)}, ${Math.round(s.centerZ)}) · ` +
      `${dist >= 1000 ? `${(dist / 1000).toFixed(1)} km` : `${Math.round(dist)} m`} from you</div>`,
    );

    for (const w of wars) {
      const otherId = Number(w.aggressorSid) === sid ? Number(w.defenderSid) : Number(w.aggressorSid);
      const other = this.siteById(otherId - 1);
      const mine = Number(w.aggressorSid) === sid;
      html.push(
        `<div style="margin-top:6px;padding:5px 8px;border:1px solid color-mix(in srgb, var(--ax-bad) 34%, var(--ax-line));border-radius:var(--ax-radius);color:var(--ax-bad)">` +
        `⚔ AT WAR with <button data-goto="${otherId - 1}" style="background:none;border:none;color:var(--ax-bad);text-decoration:underline;cursor:pointer;padding:0">${other ? esc(this.nameOf(other)) : `#${otherId}`}</button>` +
        ` <span style="opacity:.8">(${mine ? 'aggressor' : 'defender'} · ${w.casualties} fallen · exhaustion ${mine ? w.exhaustionA : w.exhaustionB}/100 · ${ago(w.startedAt)})</span></div>`,
      );
    }

    // Governance
    html.push(this.h('governance'));
    if (econ) {
      const mayor = agentById.get(String(econ.mayorId));
      const mayorName = mayor
        ? `the ${esc(mayor.job)}${memberHouse.has(String(mayor.versonaId)) ? ` of the ${esc(memberHouse.get(String(mayor.versonaId))!)} household` : ''}`
        : Number(econ.mayorId) === 0 ? '<span style="opacity:.55">none</span>' : `#${econ.mayorId}`;
      html.push(this.kv('mayor', mayorName));
      html.push(this.kv('sales tax', `${(econ.taxBps / 100).toFixed(1)}%`));
      html.push(this.kv('next election', inH(econ.nextElectionAt)));
      if (econ.projectKey !== '') html.push(this.kv('public works', `${esc(pretty(econ.projectKey))} — ${econ.projectProgress}/${econ.projectTarget}`));
    } else html.push('<div style="opacity:.5">no economy row yet — settle in and the ledger begins</div>');
    for (const o of offices) {
      html.push(this.kv(OFFICE_LABEL[o.office] ?? esc(officeLabel(o.office)), `${esc(o.holderName)} <span style="opacity:.5">(${ago(o.sinceAt)})</span>`));
    }
    if (residents.length > 0) html.push(this.kv('player residents', String(residents.length)));

    html.push(this.societyHtml(sid));

    // Laws
    const activeClauses = clauses.filter((cl) => cl.status !== 'rejected');
    if (activeClauses.length + norms.length > 0) {
      html.push(this.h('laws & customs'));
      for (const cl of activeClauses.slice(0, 8)) {
        html.push(this.kv(`charter · ${esc(pretty(cl.kind))}`, `${cl.value}${cl.status === 'active' ? '' : ` <span style="opacity:.55">(${esc(cl.status)})</span>`}`));
      }
      for (const n of norms.filter((x) => x.active).slice(0, 6)) {
        html.push(this.kv(`custom · ${esc(pretty(n.key))}`,
          `<span title="${esc(n.text)}">${esc(n.text.length > 46 ? `${n.text.slice(0, 44)}…` : n.text)}</span> <span style="opacity:.55">${Math.round(n.adoptionX10 / 10)}% hold to it</span>`));
      }
    }

    // People
    html.push(this.h('people'));
    html.push(this.kv('population', String(Math.max(agents.length, versonas.length)) + (agents.length === 0 ? ' <span style="opacity:.5">(loading…)</span>' : '')));
    if (agents.length > 0) {
      const jobs = new Map<string, number>();
      for (const a of agents) jobs.set(a.job, (jobs.get(a.job) ?? 0) + 1);
      html.push(this.kv('trades', [...jobs.entries()].sort((x, y) => y[1] - x[1]).map(([j, n]) => `${esc(j)} ×${n}`).join(', ')));
      const avg = (f: (a: AgentRow) => number): number => Math.round(agents.reduce((t, a) => t + f(a), 0) / agents.length);
      html.push(this.kv('wellbeing', `mood ${this.bar(avg((a) => a.mood), 'var(--ax-good)')} · hunger ${this.bar(avg((a) => a.hunger), 'var(--ax-warn)')}`));
      html.push(this.kv('coin among folk', coin(agents.reduce((t, a) => t + Number(a.wallet), 0))));
    }
    if (households.length > 0) {
      html.push(this.kv('households', `${households.length} — ${households.slice(0, 5).map((h2) => esc(h2.surname)).join(', ')}${households.length > 5 ? '…' : ''}`));
    }
    const faithCounts = new Map<string, number>();
    for (const a of agents) {
      const f = faiths.get(String(a.versonaId));
      if (f) faithCounts.set(f, (faithCounts.get(f) ?? 0) + 1);
    }
    if (faithCounts.size > 0) {
      html.push(this.kv('faiths', [...faithCounts.entries()].map(([f, n]) => `${esc(pretty(f))} ×${n}`).join(', ')));
    }
    if (graves.length > 0) {
      const last = graves[graves.length - 1];
      html.push(this.kv('graves', `${graves.length} — last: ${esc(last.name)} <span style="opacity:.55">(${esc(last.cause)}, ${ago(last.diedAt)})</span>`));
    }

    // Economy
    html.push(this.h('economy'));
    if (econ) {
      html.push(this.kv('treasury', coin(econ.treasury)));
      html.push(this.kv('food', `${econ.stockFood} in store · ${(econ.priceFood / 100).toFixed(2)} ⓒ`));
      html.push(this.kv('ore', `${econ.stockOre} · ${(econ.priceOre / 100).toFixed(2)} ⓒ`));
      html.push(this.kv('tools', `${econ.stockTools} · ${(econ.priceTools / 100).toFixed(2)} ⓒ`));
      html.push(this.kv('luxury', `${econ.stockLuxury} · ${(econ.priceLuxury / 100).toFixed(2)} ⓒ`));
    }
    if (exchange) html.push(this.kv('exchange office', 'SOV ↔ trade crates (U)'));
    if (slots.length > 0) {
      const open = slots.filter((sl) => Number(sl.holderId) === 0);
      html.push(this.kv('labor', `${slots.length - open.length}/${slots.length} posts filled` +
        (open.length ? ` — hiring: ${open.slice(0, 3).map((sl) => `${esc(pretty(sl.fn))} (${sl.wage}ⓒ)`).join(', ')}` : '')));
    }
    if (stock.length > 0) {
      const rows = [...stock].sort((a, b) => b.qty - a.qty);
      html.push(this.kv(`goods in town (${rows.length})`, rows.slice(0, 12).map((st) => `${esc(pretty(st.commodity))} ${Math.round(st.qty)}`).join(' · ')));
    }
    if (rounds.length > 0) {
      const latest = new Map<string, RoundRow>();
      for (const r of rounds) if ((latest.get(r.commodity)?.epoch ?? -1) < r.epoch) latest.set(r.commodity, r);
      const line = [...latest.values()].sort((a, b) => b.volume - a.volume).slice(0, 8)
        .map((r) => `${esc(pretty(r.commodity))} ${r.avgPrice.toFixed(1)}ⓒ×${r.volume}${r.unfilled > 0 ? ` <span style="opacity:.5">(+${r.unfilled} unmet)</span>` : ''}`);
      html.push(this.kv('last market day', line.join(' · ')));
    }

    // Rivals
    if (rivals.length > 0) {
      html.push(this.h('grievances'));
      for (const r of rivals.slice(0, 5)) {
        const otherId = (Number(r.aId) === sid ? Number(r.bId) : Number(r.aId)) - 1;
        const other = this.siteById(otherId);
        html.push(this.kv(
          `<button data-goto="${otherId}" style="background:none;border:none;color:var(--ax-soft);text-decoration:underline;cursor:pointer;padding:0">${other ? esc(this.nameOf(other)) : `#${otherId}`}</button>`,
          `${this.bar(r.grievance, r.grievance >= 60 ? 'var(--ax-bad)' : 'var(--ax-warn)')} ${r.grievance}/100`));
      }
    }

    // The folk themselves
    html.push(this.h(`the folk (${Math.max(agents.length, versonas.length)})`));
    if (agents.length === 0) {
      html.push('<div style="opacity:.5">meeting the townsfolk…</div>');
    }
    const stateOf = new Map(versonas.map((v) => [String(v.versonaId), v.state]));
    for (const a of [...agents].sort((x, y) => Number(y.wallet) - Number(x.wallet)).slice(0, 48)) {
      const key = String(a.versonaId);
      const house = memberHouse.get(key);
      const faith = faiths.get(key);
      html.push(
        `<div style="padding:2.5px 0;border-bottom:1px solid var(--ax-line)">` +
        `<span style="color:var(--ax-text)">the ${esc(a.job)}</span>` +
        `${house ? ` <span style="opacity:.8">of the ${esc(house)} household</span>` : ` <span style="opacity:.4">#${key}</span>`}` +
        `${faith ? ` <span style="color:var(--ax-rare)" title="${esc(pretty(faith))}">◈</span>` : ''}` +
        `<div style="display:flex;gap:8px;font-size:11px;opacity:.85;align-items:center">` +
        `<span>${coin(a.wallet)}</span>` +
        `<span>mood ${this.bar(a.mood, 'var(--ax-good)')}</span>` +
        `<span>hunger ${this.bar(a.hunger, 'var(--ax-warn)')}</span>` +
        `${stateOf.has(key) ? `<span style="opacity:.7">${esc(stateOf.get(key)!)}</span>` : ''}` +
        `</div></div>`,
      );
    }
    if (agents.length > 48) html.push(`<div style="opacity:.5;padding:3px 0">…and ${agents.length - 48} more</div>`);
    return html.join('');
  }

  private civHtml(): string {
    this.ensureCivTabSubs();
    const agents = this.tbl<AgentRow>('npcAgent');
    const econs = this.tbl<EconRow>('settlementEcon');
    const graves = this.tbl<GraveRow>('grave');
    const wars = this.tbl<WarRow>('warState').filter((w) => w.status === 'declared');
    const civBySid = new Map<number, Civilization>();
    for (const s of this.sites) civBySid.set(s.id, this.civOf(s));

    const html: string[] = [];
    for (const civ of ['a', 'b'] as Civilization[]) {
      const mySites = this.sites.filter((s) => civBySid.get(s.id) === civ);
      const tiers = { city: 0, town: 0, hamlet: 0, capital: 0, district: 0, circuit: 0 } as Record<string, number>;
      for (const s of mySites) tiers[s.tier]++;
      const sidSet = new Set(mySites.map((s) => dbSid(s.id)));
      const myAgents = agents.filter((a) => sidSet.has(Number(a.settlementId)));
      const myEcons = econs.filter((e) => sidSet.has(Number(e.settlementId)));
      const myGraves = graves.filter((g2) => sidSet.has(Number(g2.settlementId)));
      const myWars = wars.filter((w) => sidSet.has(Number(w.aggressorSid)) || sidSet.has(Number(w.defenderSid)));

      html.push(
        `<div style="margin-top:6px;padding:7px 9px;border:1px solid ${CIV_COLOR[civ]}44;border-radius:var(--ax-radius)">` +
        `<div style="font:700 13px var(--ax-font);color:${CIV_COLOR[civ]}">● ${CIV_TITLE[civ]}</div>` +
        `<div style="opacity:.6;font-size:11px;margin-bottom:4px">the ${CIV_FOLK[civ]}</div>`,
      );
      html.push(this.kv('settlements', `${mySites.length} — ${tiers.city} cities · ${tiers.town} towns · ${tiers.hamlet} hamlets`));
      if (myAgents.length > 0) {
        const jobs = new Map<string, number>();
        for (const a of myAgents) jobs.set(a.job, (jobs.get(a.job) ?? 0) + 1);
        const avgMood = Math.round(myAgents.reduce((t, a) => t + a.mood, 0) / myAgents.length);
        const avgHunger = Math.round(myAgents.reduce((t, a) => t + a.hunger, 0) / myAgents.length);
        html.push(this.kv('population', String(myAgents.length)));
        html.push(this.kv('coin among folk', coin(myAgents.reduce((t, a) => t + Number(a.wallet), 0))));
        html.push(this.kv('wellbeing', `mood ${this.bar(avgMood, 'var(--ax-good)')} ${avgMood} · hunger ${this.bar(avgHunger, 'var(--ax-warn)')} ${avgHunger}`));
        html.push(this.kv('trades', [...jobs.entries()].sort((x, y) => y[1] - x[1]).slice(0, 6).map(([j, n]) => `${esc(j)} ×${n}`).join(', ')));
      } else {
        html.push('<div style="opacity:.5;padding:2px 0">counting the population…</div>');
      }
      if (myEcons.length > 0) {
        html.push(this.kv('treasuries', coin(myEcons.reduce((t, e) => t + Number(e.treasury), 0))));
        const richest = [...myEcons].sort((x, y) => Number(y.treasury) - Number(x.treasury))[0];
        const rs = this.siteById(Number(richest.settlementId) - 1);
        if (rs) html.push(this.kv('wealthiest', `<button data-goto="${rs.id}" style="background:none;border:none;color:var(--ax-soft);text-decoration:underline;cursor:pointer;padding:0">${esc(this.nameOf(rs))}</button> (${coin(richest.treasury)})`));
      }
      if (myGraves.length > 0) html.push(this.kv('the honored dead', String(myGraves.length)));
      html.push(this.kv('wars', myWars.length === 0 ? '<span style="opacity:.6">at peace</span>' : `<span style="color:var(--ax-bad)">${myWars.length} active</span>`));
      html.push('</div>');
    }

    if (wars.length > 0) {
      html.push(this.h('active wars'));
      for (const w of wars) {
        const a = this.siteById(Number(w.aggressorSid) - 1), d = this.siteById(Number(w.defenderSid) - 1);
        html.push(this.kv(
          `${a ? esc(this.nameOf(a)) : '?'} ⚔ ${d ? esc(this.nameOf(d)) : '?'}`,
          `${w.casualties} fallen · ${ago(w.startedAt)}`));
      }
    }
    html.push(`<div style="opacity:.45;font-size:11px;margin-top:8px">312-site census derives from the world seed; live rows stream from the server as they change.</div>`);
    return html.join('');
  }
}
